import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../components/ThemeSwitcher/ThemeSwitcher";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Query from "@arcgis/core/rest/support/Query";
import * as query from "@arcgis/core/rest/query";
import DivLoader from '../../components/common/Loader/DivLoader';
import useScripts from '../../utils/UseScripts';
import './AnimalApprovedRecords.css';
import { FiEdit2 } from "react-icons/fi";
import AlertMessage from "../../components/common/Alert/AlertMessage";
import { BiFilterAlt } from 'react-icons/bi';
import { ImCancelCircle } from "react-icons/im";
import { RxCalendar } from "react-icons/rx";
import Select from "react-select";
import CustomDropdownIndicator from "../../components/common/CustomDropdownIndicator";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point.js";
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { showAlert } from "../../utils/Helpers";
import "react-tooltip/dist/react-tooltip.css";
import { Tooltip } from "react-tooltip";
import CommonFunctions from "./CommonFunctions";
import { Modal, Button } from "react-bootstrap";
import AnimalSightingMobileApproved from "./AnimalSightingMobile/AnimalSightingMobileApproved";
import { RxCross2 } from "react-icons/rx";
import DOMPurify from "dompurify";
import { useMapServices } from "../../components/MapServiceContext";
import { getLocalStorageItem } from "../../utils/HelperFunctions";
import esriConfig from "@arcgis/core/config.js";
import { applyTheme, updateAllCharts, updateImages } from "../../utils/theme";
import { useEDPContext } from "../../components/EDPContext";
import { useUserGroups } from "../../../src/components/common/Auth/UserGroupsContext";
import { createPortal } from "react-dom";
let elementInstances = {}
let editingRows = [];

export const fetchApprovedRecordsByApprovalType = async ({
  type,
  layerUrl,
  fieldsMapping,
  SurveyFromDate,
  SurveyToDate,
  t,
  setGlobalData,
  currentRowsRef,
  setData,
  setHeaders
}) => {
  try {
    const token = localStorage.getItem("PortalToken");
    const queryparams = new Query();
    queryparams.returnGeometry = true;
    queryparams.outFields = ['*'];

    if (SurveyFromDate !== "" && SurveyToDate !== "") {
      if (new Date(SurveyFromDate) < new Date(SurveyToDate)) {
        const fromDate = new Date(SurveyFromDate).toISOString().split("T")[0];
        const toDate = new Date(SurveyToDate).toISOString().split("T")[0];
        if (type === t("Invertebrates") || type === t("Reptiles")) {
          queryparams.where = `ObservationDate >= '${fromDate}' AND ObservationDate <= '${toDate}'`;
        }
        else {
          queryparams.where = `SurveyDate >= '${fromDate}' AND SurveyDate <= '${toDate}'`;
        }
      } else {
        showAlert(t("Please select valid dates"), "error");
        return;
      }
    } else {
      queryparams.where = "1=1"; // Default to no filter if no dates are provided
    }
    queryparams.orderByFields = ["created_date DESC"];
    const requestOptions = {
      query: {
        token: token,
      },
    };
    const approvedRecords = await query.executeQueryJSON(layerUrl, queryparams, requestOptions);
    //const approvedRecords = await query.executeQueryJSON(layerUrl, queryparams);
    if (approvedRecords.features.length > 0) {
      let DisplayAttributes = fieldsMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      let DisplayHeaders = fieldsMapping.filter(item => item.Mandatory === true && item.fieldname !== "OBJECTID" && item.fieldname !== "SpeciesOrder").map(item => item.Value);

      let data = approvedRecords.features.map((item, index) => {
        let filteredItem = {};
        DisplayAttributes.forEach(attr => {
          if (["Point_X", "POINT_X"].includes(attr)) {
            filteredItem[attr] = item.geometry?.longitude?.toFixed(5) || "";
          } else if (["Point_Y", "POINT_Y"].includes(attr)) {
            filteredItem[attr] = item.geometry?.latitude?.toFixed(5) || "";
          } else if (attr in item.attributes) {
            if (type === t("Invertebrates") && attr === "Species_Order") {
              filteredItem[attr] = item.attributes[attr].trim();
            }
            else {
              filteredItem[attr] = item.attributes[attr];
            }
          }

        });

        return { id: index + 1, ...filteredItem };
      });

      if (type === t("Plants")) {
        currentRowsRef.current = [];
        setData(approvedRecords.features);
        setHeaders(DisplayHeaders);
        currentRowsRef.current = data;

        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        PlantsData: approvedRecords.features, // Directly storing features
                        PlantsHeaders: DisplayHeaders, // Storing headers as separate
                        PlantscurrentRows: data // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
      else if (type === t("Invertebrates")) {
        currentRowsRef.current = [];
        setData(approvedRecords.features);
        setHeaders(DisplayHeaders);
        currentRowsRef.current = data;

        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        InvertebratesData: approvedRecords.features, // Directly storing features
                        InvertebrateHeaders: DisplayHeaders, // Storing headers as separate
                        InvertebratescurrentRows: data // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
      else if (type === t("Mammals")) {
        currentRowsRef.current = [];
        setData(approvedRecords.features);
        setHeaders(DisplayHeaders);
        currentRowsRef.current = data;

        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        MammalsData: approvedRecords.features, // Directly storing features
                        MammalsHeaders: DisplayHeaders, // Storing headers as separate
                        MammalscurrentRows: data // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
      else if (type === t("Reptiles")) {
        currentRowsRef.current = [];
        setData(approvedRecords.features);
        setHeaders(DisplayHeaders);
        currentRowsRef.current = data;

        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        ReptilesData: approvedRecords.features, // Directly storing features
                        ReptilesHeaders: DisplayHeaders, // Storing headers as separate
                        ReptilescurrentRows: data // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
    }
    else {
      if (type === t("Plants")) {
        currentRowsRef.current = [{ message: "No data found" }];
        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        PlantscurrentRows: [{ message: "No data found" }] // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
      else if (type === t("Invertebrates")) {
        currentRowsRef.current = [{ message: "No data found" }];
        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        InvertebratescurrentRows: [{ message: "No data found" }] // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
      else if (type === t("Mammals")) {
        currentRowsRef.current = [{ message: "No data found" }];
        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        MammalscurrentRows: [{ message: "No data found" }] // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
      else if (type === t("Reptiles")) {
        currentRowsRef.current = [{ message: "No data found" }];
        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        ReptilescurrentRows: [{ message: "No data found" }] // Storing rows separately
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      }
      showAlert(t('noData'), "info");
    }
  } catch (error) {
    showAlert("Error fetching data", "error");
  }
}

const AnimalApprovedRecords = ({ fullView, ...props }) => {
  const { mapview } = props;
  const { t, i18n } = useTranslation();
  const { groupNames, groupFunctions, groupServices } = useUserGroups();
  let { theme } = useTheme();
  theme = localStorage.getItem("theme") || theme;
  const { globalDataContextValue } = useEDPContext();
  const { globalData, setGlobalData } = globalDataContextValue;
  const [loading, setLoading] = useState(false);
  var AnimalSightingApprovedTab = globalData.find(module => module.moduleName === "Animal Sighting")
    .subTabs.find(subTab => subTab.name === "ApprovedRecords")
  // const [activeApprovedtabs, setActiveApprovedtab] = useState(AnimalSightingApprovedTab.activeApprovedtabs ? AnimalSightingApprovedTab.activeApprovedtabs : t("Invertebrates"))

  const groupMap = {
    [t("Invertebrates")]: window.userGroups.invertebrates,
    [t("Plants")]: window.userGroups.plants,
    [t("Mammals")]: window.userGroups.mammals,
    [t("Reptiles")]: window.userGroups.reptiles,
  };

  const approvedRecordList = [t('Invertebrates'), t('Plants'), t('Mammals'), t('Reptiles')]
  const filteredApprovedList = approvedRecordList.filter(
    (item) => groupNames.includes(groupMap[item])
  );

 const [activeApprovedtabs, setActiveApprovedtab] = useState(
    filteredApprovedList.length > 0 ? filteredApprovedList[0] : ""
  );
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentEdit, setCurrentEdit] = useState({ rowId: null, key: null });
  const [PlantsData, setPlantsData] = useState([]);
  const [InvertebratesData, setInvertebratesData] = useState([]);
  const [MammalsData, setMammalsData] = useState([]);
  const [ReptilesData, setReptilesData] = useState([]);
  // Server-side totals for tab badges (from returnCountOnly / executeForCount)
  const [PlantsCount, setPlantsCount] = useState(0);
  const [InvertebratesCount, setInvertebratesCount] = useState(0);
  const [MammalsCount, setMammalsCount] = useState(0);
  const [ReptilesCount, setReptilesCount] = useState(0);
  const getInitialSelectedRows = (category) => {
    return (
      globalData
        .find(m => m.moduleName === "Animal Sighting")
        ?.subTabs.find(st => st.name === "ApprovedRecords")
        ?.selectedRows?.[category] ?? []
    );
  };

  const [PlantsSelectedRows, setPlantsSelectedRows] = useState(() => getInitialSelectedRows("Plants"));
  const [InvertebratesSelectedRows, setInvertebratesSelectedRows] = useState(() => getInitialSelectedRows("Invertebrates"));
  const [MammalsSelectedRows, setMammalsSelectedRows] = useState(() => getInitialSelectedRows("Mammals"));
  const [ReptilesSelectedRows, setReptilesSelectedRows] = useState(() => getInitialSelectedRows("Reptiles"));
  const PlantscurrentRows = useRef([]);
  const InvertebratescurrentRows = useRef([]);
  const MammalscurrentRows = useRef([]);
  const ReptilescurrentRows = useRef([]);
  const [widgetMobEl, setWidgetMobEl] = useState(null);
  const [alertmessage, setAlertMessage] = useState(null);
  const [invertebrateheaders, setInvertebrateHeaders] = useState([]);
  const [plantsheaders, setPlantsHeaders] = useState([]);
  const [reptilesheaders, setReptilesHeaders] = useState([]);
  const [mammalsheaders, setMammalsHeaders] = useState([]);
  const [filters, setFilters] = useState({});
  const [showfilter, setShowFilter] = useState(false);
  const [showDateRange, setShowDateRange] = useState(false);
  const [PlantsDomainValues, setPlantsDomainValues] = useState([]);
  const [MammalsDomainValues, setMammalsDomainValues] = useState([]);
  const [ReptilesDomainValues, setReptilesDomainValues] = useState([]);
  const [InsectsDomainValues, setInsectsDomainValues] = useState([]);
  const [ReptileSpeciesNames, setReptileSpeciesNames] = useState([]);
  const [MammalsSpeciesNames, setMammalsSpeciesNames] = useState([]);
  const [speciesOrder, setSpeciesOrder] = useState([]);
  const [familyName, setFamilyName] = useState([]);
  const [OrderFamilyNames, setOrderFamilyNames] = useState([]);
  const [FamilySpeciesNames, setFamilySpeciesNames] = useState([]);
  const [DropdownSpeciesValues, setDropdownSpeciesValues] = useState([]);
  const [DropdownFamilyValues, setDropdownFamilyValues] = useState([]);
  const [OrderwithFamily, setOrderwithFamily] = useState([]);
  const [FamilywithSpecies, setFamilywithSpecies] = useState([]);
  const [CloneData, setCloneData] = useState([]);
  const [dropdownValues, setDropdownValues] = useState([]);
  const [plantsdropdownValues, setPlantsDropdownValues] = useState([]);
  const [mammalsdropdownValues, setMammalsDropdownValues] = useState([]);
  const [reptilesdropdownValues, setReptilesDropdownValues] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [insectselectedfamily, setInsectsSelectedFamily] = useState(null);
  const [insectselectedorder, setInsectsSelectedOrder] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isDataModified, setIsDataModified] = useState(false);
  const [activeButton, setActiveButton] = useState(null);
  const [isResetBtnShow, setIsResetBtnShow] = useState(false);
  const initialInsectState = {
    SpeciesOrder: '',
    Species_Order: '',
    SpeciesName: '',
    SpeciesFamily: '',
    Status: '',
    LocationName: '',
    HabitatStatus: '',
    Habitat: '',
    MethodUsed: '',
    Researcher: '',
    NumberOfIndividuals: '',
    POINT_X: '',
    POINT_Y: '',
    Notes: '',
    ObservationDate: '',
    PhotoNumber: '',
    SpecimenNo: '',
    ID: ''
  }
  const [ApprovedInsectsData, setApprovedInsectsData] = useState(initialInsectState);
  const initialPlantState = {
    LatinName: '',
    Phenology: '',
    Location: '',
    Habitat: '',
    GrazingSigns: '',
    Precipitation: '',
    PercentageCover: '',
    SpecimenCollected: '',
    SurveyDate: '',
    Notes: '',
    Recorder: '',
  }
  const [ApprovedPlantsDataObj, setApprovedPlantsDataObj] = useState(initialPlantState);
  const initialMammalsState = {
    SpeciesName: '',
    NumberofIndividuals: '',
    SightingType: '',
    Habitat: '',
    LocationName: '',
    SurveyDate: '',
    Point_Y: '',
    Point_X: '',
    Notes: '',
    CommonName: '',
    LatinName: '',
    Recorder: '',
    SurveyTime: '',
    AM_PM: '',
    Phase: '',
    Moon_Phase: '',
    CollectionMethod: '',
    Activity_phase: '',
  }
  const [MammalsDataObj, setMammalsDataObj] = useState(initialMammalsState);
  const initialReptilesState = {
    Species: '',
    Habitat: '',
    MicroHabitat: '',
    NumberObserved: '',
    SightingType: '',
    LocationName: '',
    CommonName: '',
    LatinName: '',
    ObservationDate: '',
    SurveyTime: '',
    Recorder: '',
    Notes: '',
    Point_X: '',
    Point_Y: ''
  }
  const [ReptilesDataObj, setReptilesDataObj] = useState(initialReptilesState);
  const [datefilters, setDateFilters] = useState({
    SurveyFromDate: "",
    SurveyToDate: ""
  });
  const [isReplacedValue, setIsReplacedValue] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const AnimalSightingTab = globalData.find(module => module.moduleName === "Animal Sighting")
    ?.subTabs.find(subTab => subTab.name === "PendingRecords");
  const shouldFetchDomainValues = !AnimalSightingTab?.data?.InsectsDomainValues ||
    !AnimalSightingTab?.data?.PlantsDomainValues ||
    !AnimalSightingTab?.data?.MammalsDomainValues ||
    !AnimalSightingTab?.data?.ReptilesDomainValues ||
    !AnimalSightingTab?.data?.SpeciesOrder ||
    !AnimalSightingTab?.data?.FamilyName;
  // const { GetDomainValues } = CommonFunctions({
  //   setInsectsDomainValues,
  //   setPlantsDomainValues,
  //   setMammalsDomainValues,
  //   setReptilesDomainValues,
  //   setSpeciesOrder,
  //   setFamilyName,
  //   setMammalsSpeciesNames,
  //   setReptileSpeciesNames,
  //   t,
  //   setLoading,
  // });

  const domainFunctions = useMemo(() => {
    if (shouldFetchDomainValues) {
      return CommonFunctions({
        setInsectsDomainValues,
        setPlantsDomainValues,
        setMammalsDomainValues,
        setReptilesDomainValues,
        setSpeciesOrder,
        setFamilyName,
        setMammalsSpeciesNames,
        setReptileSpeciesNames,
        t,
        setLoading,
      });
    }
    else {
      const AnimalSightingTab = globalData.find(module => module.moduleName === "Animal Sighting")
        ?.subTabs.find(subTab => subTab.name === "PendingRecords");
      setInsectsDomainValues([AnimalSightingTab.data.InsectsDomainValues] || []);
      setPlantsDomainValues([AnimalSightingTab.data.PlantsDomainValues] || []);
      setMammalsDomainValues([AnimalSightingTab.data.MammalsDomainValues] || []);
      setReptilesDomainValues([AnimalSightingTab.data.ReptilesDomainValues] || []);
      setSpeciesOrder(AnimalSightingTab.data.SpeciesOrder || []);
      setFamilyName(AnimalSightingTab.data.FamilyName || []);
      setMammalsSpeciesNames(AnimalSightingTab.data.MammalsSpeciesNames || []);
      setReptileSpeciesNames(AnimalSightingTab.data.ReptileSpeciesNames || []);
    }
    return {}; // Prevents execution if data already exists
  }, [shouldFetchDomainValues]);
  const { registerMapServiceToken } = useMapServices();

  const updateTheme = (event) => {
    const newTheme = event.detail;
    theme = newTheme;
    // updateAllCharts(chartInstances, theme, t);
    applyTheme(elementInstances, theme, "AnimalSighting")
  };

  useEffect(() => {
    window.addEventListener("themeChanged", updateTheme);

    return () => {
      window.removeEventListener("themeChanged", updateTheme);
    };
  }, [])

  useScripts([
    `${process.env.PUBLIC_URL}/config/AnimalSightsDataEntry.js`
  ], () => {
    setScriptLoaded(true);
  });

  const interceptorAdded = useRef(false);
  const token = localStorage.getItem("PortalToken");
  useEffect(() => {
    setGlobalData(prev =>
      prev.map(m =>
        m.moduleName === 'Animal Sighting'
          ? {
            ...m,
            subTabs: m.subTabs.map(st =>
              st.name === 'ApprovedRecords'
                ? {
                  ...st,
                  /* keep selections from every category in one place */
                  selectedRows: {
                    Plants: PlantsSelectedRows,
                    Invertebrates: InvertebratesSelectedRows,
                    Mammals: MammalsSelectedRows,
                    Reptiles: ReptilesSelectedRows,
                  },
                }
                : st
            ),
          }
          : m
      )
    );
  }, [
    PlantsSelectedRows,
    InvertebratesSelectedRows,
    MammalsSelectedRows,
    ReptilesSelectedRows,
  ]);
  useEffect(() => {
    setGlobalData(prev =>
      prev.map(m =>
        m.moduleName === 'Animal Sighting'
          ? {
            ...m,
            subTabs: m.subTabs.map(st =>
              st.name === 'ApprovedRecords'
                ? {
                  ...st,
                  /* keep selections from every category in one place */
                  activeApprovedtabs: activeApprovedtabs
                }
                : st
            ),
          }
          : m
      )
    );
  }, [
    activeApprovedtabs,
  ]);
  useEffect(() => {
    if (!scriptLoaded || interceptorAdded.current) return;

    if (token) {
      const escapedBase = window.animalSightingBaseUrl.replace(/\//g, "\\/");
      const regex = new RegExp(`${escapedBase}.+\\/FeatureServer(\\/\\d+)?(\\/\\w+)?`, "i");

      esriConfig.request.interceptors.push({
        urls: regex,
        // use the BeforeInterceptorCallback to add token to query
        before: function (params) {
          if (params && params.requestOptions) {
            // Explicitly prevent IdentityManager token injection
            params.requestOptions.authMode = "anonymous";
            params.requestOptions.query = {
              ...params.requestOptions.query,
              token
            };
          }
        },
      });
    }

    interceptorAdded.current = true;
  }, [scriptLoaded, token]);

  useEffect(() => {
    if (!mapview) return;
    if (!window.PlantInfo || !window.InvertiblesInfo || !window.MammalsInfo || !window.ReptilesInfo) return;

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);

      const hasPlantAccess = groupNames.includes(window.userGroups.plants);
      const hasInvertebrateAccess = groupNames.includes(window.userGroups.invertebrates);
      const hasMammalsAccess = groupNames.includes(window.userGroups.mammals);
      const hasReptilesAccess = groupNames.includes(window.userGroups.reptiles);

      try {
        const divSpatialEdit = document.getElementById("divSpatialEdit");
        if (divSpatialEdit) mapview.ui.remove(divSpatialEdit);

        if (shouldFetchDomainValues && typeof domainFunctions.GetDomainValues === "function") {
          await domainFunctions.GetDomainValues();
        }
        if (cancelled) return;

        const approvedTab = globalData
          .find((module) => module.moduleName === "Animal Sighting")
          ?.subTabs?.find((subTab) => subTab.name === "ApprovedRecords");
        const tabData = approvedTab?.data || {};

        const hasCachedRows = (rows) =>
          Array.isArray(rows) &&
          rows.length > 0 &&
          !(rows.length === 1 && (rows[0]?.message || rows[0]?.message));

        // Sequential fetch — avoids loading 4 large layers into memory at once
        const loadLayer = async ({
          hasAccess,
          cachedRows,
          cachedData,
          cachedHeaders,
          cachedCount,
          setData,
          setHeaders,
          setCount,
          currentRowsRef,
          type,
          layerUrl,
          fieldsMapping,
        }) => {
          if (!hasAccess || cancelled) return;

          if (hasCachedRows(cachedRows)) {
            setData(Array.isArray(cachedData) ? cachedData : cachedRows);
            if (setHeaders) setHeaders(cachedHeaders || []);
            if (setCount) setCount(cachedCount ?? cachedRows.length);
            currentRowsRef.current = cachedRows;
            return;
          }

          await fetchApprovedRecordsByType(type, layerUrl, fieldsMapping);
        };

        await loadLayer({
          hasAccess: hasPlantAccess,
          cachedRows: tabData.PlantscurrentRows,
          cachedData: tabData.PlantsData,
          cachedHeaders: tabData.PlantsHeaders,
          cachedCount: tabData.PlantsCount,
          setData: setPlantsData,
          setHeaders: setPlantsHeaders,
          setCount: setPlantsCount,
          currentRowsRef: PlantscurrentRows,
          type: t("Plants"),
          layerUrl: window.PlantInfo.TargetUrl,
          fieldsMapping: window.PlantInfo.TargetFieldMapping,
        });

        await loadLayer({
          hasAccess: hasInvertebrateAccess,
          cachedRows: tabData.InvertebratescurrentRows,
          cachedData: tabData.InvertebratesData,
          cachedHeaders: tabData.InvertebrateHeaders,
          cachedCount: tabData.InvertebratesCount,
          setData: setInvertebratesData,
          setHeaders: setInvertebrateHeaders,
          setCount: setInvertebratesCount,
          currentRowsRef: InvertebratescurrentRows,
          type: t("Invertebrates"),
          layerUrl: window.InvertiblesInfo.TargetUrl,
          fieldsMapping: window.InvertiblesInfo.TargetFieldMapping,
        });

        await loadLayer({
          hasAccess: hasMammalsAccess,
          cachedRows: tabData.MammalscurrentRows,
          cachedData: tabData.MammalsData,
          cachedHeaders: tabData.MammalsHeaders,
          cachedCount: tabData.MammalsCount,
          setData: setMammalsData,
          setHeaders: setMammalsHeaders,
          setCount: setMammalsCount,
          currentRowsRef: MammalscurrentRows,
          type: t("Mammals"),
          layerUrl: window.MammalsInfo.TargetUrl,
          fieldsMapping: window.MammalsInfo.TargetFieldMapping,
        });

        await loadLayer({
          hasAccess: hasReptilesAccess,
          cachedRows: tabData.ReptilescurrentRows,
          cachedData: tabData.ReptilesData,
          cachedHeaders: tabData.ReptilesHeaders,
          cachedCount: tabData.ReptilesCount,
          setData: setReptilesData,
          setHeaders: setReptilesHeaders,
          setCount: setReptilesCount,
          currentRowsRef: ReptilescurrentRows,
          type: t("Reptiles"),
          layerUrl: window.ReptilesInfo.TargetUrl,
          fieldsMapping: window.ReptilesInfo.TargetFieldMapping,
        });

        if (cancelled) return;
        await GetFamilyRelationTableData();
        await processRelationQueryResults();
      } catch (error) {
        console.error("Approved records load failed:", error);
        showAlert(t("dataerror"), "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [mapview, t, shouldFetchDomainValues, domainFunctions]);

  const formatToDatetimeLocal = (dateString) => {
    if (!dateString) return "";
    if (dateString.includes("T") === true) {
      const DateVal = dateString.split("T");
      const [year, month, day] = DateVal[0].split("-");
      return `${year}-${month}-${day}T${DateVal[1]}`;
    } else {
      const [day, month, yearAndTime] = dateString.split("-");
      const [year, time] = yearAndTime.split(" ");
      return `${year}-${month}-${day}T${time.slice(0, 5)}`;
    }
  };
  const fetchApprovedRecordsByType = async (type, layerUrl, fieldsMapping) => {
    try {
      //setLoading(true);
      const { SurveyFromDate, SurveyToDate } = datefilters;
      const queryparams = new Query();
      queryparams.returnGeometry = true;
      queryparams.outFields = ['*'];

      if (SurveyFromDate !== "" && SurveyToDate !== "") {
        if (new Date(SurveyFromDate) < new Date(SurveyToDate)) {
          const fromDate = new Date(SurveyFromDate).toISOString().split("T")[0];
          const toDate = new Date(SurveyToDate).toISOString().split("T")[0];
          if (type === t("Invertebrates") || type === t("Reptiles")) {
            //queryparams.where = `ObservationDate >= '${fromDate}' AND ObservationDate <= '${toDate}'`;
            queryparams.where = `ObservationDate >= '${fromDate}' AND ObservationDate <= '${toDate}'`;
          }
          else {
            //queryparams.where = `SurveyDate >= '${fromDate}' AND SurveyDate <= '${toDate}'`;
            queryparams.where = `SurveyDate >= '${fromDate}' AND SurveyDate <= '${toDate}'`;
          }
        } else {
          showAlert(t("Please select valid dates"), "error");
          setLoading(false);
          return;
        }
      } else {
        queryparams.where = "1=1"; // Default to no filter if no dates are provided
      }

      // Stable sort is required for resultOffset pagination (prevents skipped/duplicate pages)
      if (!isReplacedValue) {
        queryparams.orderByFields = ["created_date DESC", "OBJECTID DESC"];
      } else {
        queryparams.orderByFields = ["last_edited_date DESC", "OBJECTID DESC"];
        setIsReplacedValue(false);
      }
      const requestOptions = {
        query: {
          token: token,
        },
      };

      // ---------- 1) Server-side total count (for tab badge) ----------
      const BATCH_SIZE = 1000; // keep <= service maxRecordCount (often 1000 or 2000)
      let totalCount = 0;
      try {
        const countQuery = new Query();
        countQuery.where = queryparams.where;
        totalCount = await query.executeForCount(layerUrl, countQuery, requestOptions);
      } catch (countErr) {
        console.warn(`${type}: executeForCount failed`, countErr);
        totalCount = 0;
      }

      // Update tab count immediately from server total
      if (type === t("Plants")) setPlantsCount(totalCount);
      else if (type === t("Invertebrates")) setInvertebratesCount(totalCount);
      else if (type === t("Mammals")) setMammalsCount(totalCount);
      else if (type === t("Reptiles")) setReptilesCount(totalCount);

      // ---------- 2) Batch fetch ALL records via resultOffset / resultRecordCount ----------
      const allFeatures = [];
      const seenObjectIds = new Set();
      let resultOffset = 0;
      let batchIndex = 0;
      const totalBatches =
        totalCount > 0 ? Math.ceil(totalCount / BATCH_SIZE) : null;
      let exceededTransferLimit = true;
      let useObjectIdFallback = false;

      while (exceededTransferLimit) {
        batchIndex += 1;
        console.log(
          `${type}: fetching batch ${batchIndex}${
            totalBatches ? `/${totalBatches}` : ""
          } (offset=${resultOffset}, count=${BATCH_SIZE})`
        );

        const pageQuery = new Query();
        pageQuery.where = queryparams.where;
        pageQuery.outFields = queryparams.outFields;
        pageQuery.returnGeometry = queryparams.returnGeometry;
        pageQuery.orderByFields = queryparams.orderByFields;
        pageQuery.resultOffset = resultOffset;
        pageQuery.resultRecordCount = BATCH_SIZE;

        let response;
        try {
          response = await query.executeQueryJSON(
            layerUrl,
            pageQuery,
            requestOptions
          );
        } catch (batchError) {
          console.error(`${type}: batch ${batchIndex} failed`, batchError);
          showAlert("Error fetching data", "error");
          return;
        }

        const features = response?.features || [];
        if (features.length === 0) {
          exceededTransferLimit = false;
          break;
        }

        let newOnThisBatch = 0;
        for (let i = 0; i < features.length; i++) {
          const feature = features[i];
          const oid = feature.attributes?.OBJECTID ?? feature.attributes?.objectid;
          if (oid != null) {
            if (seenObjectIds.has(oid)) {
              continue;
            }
            seenObjectIds.add(oid);
          }
          allFeatures.push(feature);
          newOnThisBatch += 1;
        }

        // If offset is ignored by the service, every batch returns the same records
        if (newOnThisBatch === 0) {
          console.warn(
            `${type}: resultOffset not advancing (duplicate OBJECTIDs). Falling back to OBJECTID batching.`
          );
          useObjectIdFallback = true;
          allFeatures.length = 0;
          seenObjectIds.clear();
          break;
        }

        resultOffset += features.length;

        const reachedTotal =
          totalCount > 0 && allFeatures.length >= totalCount;
        const shortPage = features.length < BATCH_SIZE;
        const serverSaysMore = response?.exceededTransferLimit === true;
        const serverSaysDone = response?.exceededTransferLimit === false;

        // Stop when we have everything, or last page, or server says done
        if (reachedTotal || shortPage || serverSaysDone) {
          exceededTransferLimit = false;
          break;
        }

        // Continue when server says more data remains
        if (serverSaysMore) {
          exceededTransferLimit = true;
        } else if (totalCount > 0 && allFeatures.length < totalCount) {
          // Some services omit exceededTransferLimit — keep paging using totalCount
          exceededTransferLimit = true;
        } else {
          exceededTransferLimit = false;
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      // ---------- 2b) Fallback: OBJECTID list + chunk queries (when offset unsupported) ----------
      if (useObjectIdFallback) {
        let objectIds = [];
        try {
          const idsQuery = new Query();
          idsQuery.where = queryparams.where;
          objectIds = (await query.executeForIds(layerUrl, idsQuery, requestOptions)) || [];
        } catch (idsErr) {
          console.error(`${type}: executeForIds failed`, idsErr);
          showAlert("Error fetching data", "error");
          return;
        }

        if (totalCount === 0) {
          totalCount = objectIds.length;
          if (type === t("Plants")) setPlantsCount(totalCount);
          else if (type === t("Invertebrates")) setInvertebratesCount(totalCount);
          else if (type === t("Mammals")) setMammalsCount(totalCount);
          else if (type === t("Reptiles")) setReptilesCount(totalCount);
        }

        const idBatchSize = 500;
        const idBatches = Math.ceil(objectIds.length / idBatchSize) || 1;
        for (let i = 0; i < objectIds.length; i += idBatchSize) {
          const batchNo = Math.floor(i / idBatchSize) + 1;
          console.log(`${type}: fetching OBJECTID batch ${batchNo}/${idBatches}`);

          const chunkQuery = new Query();
          chunkQuery.objectIds = objectIds.slice(i, i + idBatchSize);
          chunkQuery.outFields = queryparams.outFields;
          chunkQuery.returnGeometry = queryparams.returnGeometry;

          let response;
          try {
            response = await query.executeQueryJSON(
              layerUrl,
              chunkQuery,
              requestOptions
            );
          } catch (batchError) {
            console.error(`${type}: OBJECTID batch ${batchNo} failed`, batchError);
            showAlert("Error fetching data", "error");
            return;
          }

          const features = response?.features || [];
          for (let f = 0; f < features.length; f++) {
            allFeatures.push(features[f]);
          }
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      console.log(`${type}: total fetched = ${allFeatures.length}${totalCount ? ` / count ${totalCount}` : ""}`);

      const approvedRecords = { features: allFeatures };

      // Prefer server count for tabs; fall back to fetched length
      const finalCount = totalCount > 0 ? totalCount : allFeatures.length;
      if (type === t("Plants")) setPlantsCount(finalCount);
      else if (type === t("Invertebrates")) setInvertebratesCount(finalCount);
      else if (type === t("Mammals")) setMammalsCount(finalCount);
      else if (type === t("Reptiles")) setReptilesCount(finalCount);

      if (approvedRecords.features.length > 0) {
        let DisplayAttributes = fieldsMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
        let DisplayHeaders = fieldsMapping.filter(item => item.Mandatory === true && item.fieldname !== "OBJECTID" && item.fieldname !== "SpeciesOrder").map(item => item.Value);

        let data = approvedRecords.features.map((item, index) => {
          let filteredItem = {};
          DisplayAttributes.forEach(attr => {
            if (["Point_X", "POINT_X"].includes(attr)) {
              filteredItem[attr] = item.geometry?.longitude?.toFixed(5) || "";
            } else if (["Point_Y", "POINT_Y"].includes(attr)) {
              filteredItem[attr] = item.geometry?.latitude?.toFixed(5) || "";
            } else if (attr in item.attributes) {
              if (type === t("Invertebrates") && attr === "Species_Order") {
                filteredItem[attr] = item.attributes[attr]?.trim();
              }
              else {
                filteredItem[attr] = item.attributes[attr];
              }
            }

          });

          return {
            id: index + 1,
            OBJECTID: item.attributes?.OBJECTID ?? item.attributes?.objectid,
            ...filteredItem,
          };
        });

        // Release Graphic feature references after mapping (helps GC on large layers)
        approvedRecords.features.length = 0;
        allFeatures.length = 0;

        if (type === t("Plants")) {
          setPlantsData(data);
          setPlantsHeaders(DisplayHeaders);
          setPlantsCount(finalCount);
          PlantscurrentRows.current = data;

          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          PlantsData: data,
                          PlantsHeaders: DisplayHeaders,
                          PlantscurrentRows: data,
                          PlantsCount: finalCount,
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
        else if (type === t("Invertebrates")) {
          setInvertebratesData(data);
          setInvertebrateHeaders(DisplayHeaders);
          setInvertebratesCount(finalCount);
          InvertebratescurrentRows.current = data;

          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          InvertebratesData: data,
                          InvertebrateHeaders: DisplayHeaders,
                          InvertebratescurrentRows: data,
                          InvertebratesCount: finalCount,
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
        else if (type === t("Mammals")) {
          setMammalsData(data);
          setMammalsHeaders(DisplayHeaders);
          setMammalsCount(finalCount);
          MammalscurrentRows.current = data;

          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          MammalsData: data,
                          MammalsHeaders: DisplayHeaders,
                          MammalscurrentRows: data,
                          MammalsCount: finalCount,
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
        else if (type === t("Reptiles")) {
          setReptilesData(data);
          setReptilesHeaders(DisplayHeaders);
          setReptilesCount(finalCount);
          ReptilescurrentRows.current = data;

          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          ReptilesData: data,
                          ReptilesHeaders: DisplayHeaders,
                          ReptilescurrentRows: data,
                          ReptilesCount: finalCount,
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
      }
      else {
        if (type === t("Plants")) {
          setPlantsData([]);
          setPlantsCount(0);
          PlantscurrentRows.current = [{ message: "No data found" }];
          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          PlantsData: [],
                          PlantsCount: 0,
                          PlantscurrentRows: [{ message: "No data found" }]
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
        else if (type === t("Invertebrates")) {
          setInvertebratesData([]);
          setInvertebratesCount(0);
          InvertebratescurrentRows.current = [{ message: "No data found" }];
          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          InvertebratesData: [],
                          InvertebratesCount: 0,
                          InvertebratescurrentRows: [{ message: "No data found" }]
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
        else if (type === t("Mammals")) {
          setMammalsData([]);
          setMammalsCount(0);
          MammalscurrentRows.current = [{ message: "No data found" }];
          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          MammalsData: [],
                          MammalsCount: 0,
                          MammalscurrentRows: [{ message: "No data found" }]
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
        else if (type === t("Reptiles")) {
          setReptilesData([]);
          setReptilesCount(0);
          ReptilescurrentRows.current = [{ message: "No data found" }];
          setGlobalData(prevData =>
            prevData.map(module =>
              module.moduleName === "Animal Sighting"
                ? {
                  ...module,
                  subTabs: module.subTabs.map(subTab =>
                    subTab.name === "ApprovedRecords"
                      ? {
                        ...subTab,
                        data: {
                          ...subTab.data,
                          ReptilesData: [],
                          ReptilesCount: 0,
                          ReptilescurrentRows: [{ message: "No data found" }]
                        }
                      }
                      : subTab
                  )
                }
                : module
            )
          );
        }
        showAlert(t('noData'), "info");
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      showAlert("Error fetching data", "error");
    }
  }

  const GetFamilyRelationTableData = async () => {
    var AnimalSightingTab = globalData.find(module => module.moduleName === "Animal Sighting")
      .subTabs.find(subTab => subTab.name === "ApprovedRecords")
    if (AnimalSightingTab?.data && AnimalSightingTab?.data?.OrderFamilyNames && AnimalSightingTab?.data?.FamilySpeciesNames) {
      setOrderFamilyNames(AnimalSightingTab?.data?.OrderFamilyNames);
      setFamilySpeciesNames(AnimalSightingTab?.data?.FamilySpeciesNames);
    }
    else {
      try {
        const queryparams = new Query({
          where: "1=1",
          outFields: ["*"],
          orderByFields: ["Family ASC"],
          returnGeometry: false,
        });
        const requestOptions = {
          query: {
            token: token,
          },
        };
        const invertebrateSortedRecords = await query.executeQueryJSON(window.InvertiblesInfo.DomainNameUrl, queryparams, requestOptions);
        //const invertebrateSortedRecords = await query.executeQueryJSON(window.InvertiblesInfo.DomainNameUrl, queryparams);

        const resultCount = invertebrateSortedRecords.features;
        const SpecieswithFamilyArray = [];
        const FamilyforOrderArray = [];
        for (let y = 0; y < resultCount.length; y++) {
          const newSpeciesName = resultCount[y].attributes.Scientific_Name?.trim() || ''; // Handle potential null/undefined      
          const familyName = resultCount[y].attributes.Family?.trim(); // Handle potential null/undefined
          const specsorder = resultCount[y].attributes.Order_?.trim();
          if (!specsorder || !familyName) continue;    // Skip if either order or family name is missing

          const orderEntry = FamilyforOrderArray.find(order => order.SpeciesOrder === specsorder);
          if (orderEntry) {
            if (!orderEntry.FamilyNames.some(family => family.value === familyName)) {
              orderEntry.FamilyNames.push({ key: familyName, value: familyName }); // Correct object syntax
            }
          } else {
            FamilyforOrderArray.push({
              SpeciesOrder: specsorder, // Correct object syntax
              FamilyNames: [{ key: familyName, value: familyName }] // Correct object syntax
            });
          }

          const familyEntry = SpecieswithFamilyArray.find(family => family.FamilyName === familyName);
          if (familyEntry) {
            if (!familyEntry.SpeciesNames.some(species => species.value === newSpeciesName) && newSpeciesName) {
              familyEntry.SpeciesNames.push({ key: newSpeciesName, value: newSpeciesName }); // Correct object syntax
            }
          } else {
            if (newSpeciesName) {
              SpecieswithFamilyArray.push({
                FamilyName: familyName, // Correct object syntax
                SpeciesNames: [{ key: newSpeciesName, value: newSpeciesName }] // Correct object syntax
              });
            }
          }
        }
        setOrderFamilyNames(FamilyforOrderArray);
        setFamilySpeciesNames(SpecieswithFamilyArray);

        setGlobalData(prevData =>
          prevData.map(module =>
            module.moduleName === "Animal Sighting"
              ? {
                ...module,
                subTabs: module.subTabs.map(subTab =>
                  subTab.name === "ApprovedRecords"
                    ? {
                      ...subTab,
                      data: {
                        ...subTab.data,
                        OrderFamilyNames: FamilyforOrderArray,
                        FamilySpeciesNames: SpecieswithFamilyArray
                      }
                    }
                    : subTab
                )
              }
              : module
          )
        );
      } catch (error) {
        showAlert("Error query featrues", "error");
        setLoading(false);
      }
    }
  };

  const processRelationQueryResults = async () => {
    var AnimalSightingTab = globalData.find(module => module.moduleName === "Animal Sighting")
      .subTabs.find(subTab => subTab.name === "ApprovedRecords")
    if (AnimalSightingTab?.data && AnimalSightingTab?.data?.OrderwithFamily && AnimalSightingTab?.data?.FamilySpeciesNames) {
      setOrderwithFamily(AnimalSightingTab?.data?.OrderwithFamily);
      setFamilywithSpecies(AnimalSightingTab?.data?.FamilywithSpecies);
    }
    else {
      const queryparams = new Query({
        where: "1=1",
        outFields: ["*"],
        orderByFields: ["Order_ ASC"],
        returnGeometry: true,
      });
      const requestOptions = {
        query: {
          token: token,
        },
      };
      const insectsSortedRecords = await query.executeQueryJSON(window.InvertiblesInfo.DomainNameUrl, queryparams, requestOptions);
      //const insectsSortedRecords = await query.executeQueryJSON(window.InvertiblesInfo.DomainNameUrl, queryparams);
      let FamilywithOrderArray = [];
      let SpecieswithFamilyArray = [];
      let ordertempname = "";
      let tempname = "";

      insectsSortedRecords.features.forEach((feature) => {
        const { Order_, Family, Scientific_Name } = feature.attributes;
        const trimmedOrder = Order_.trim();
        const trimmedFamily = Family.trim();
        const trimmedScientificName = Scientific_Name.trim();

        // Processing Order with Family
        if (ordertempname === trimmedOrder) {
          const existingOrder = FamilywithOrderArray.find(
            (order) => order.SpeciesOrder === trimmedOrder
          );
          if (existingOrder) {
            if (!existingOrder.FamilyName.includes(trimmedFamily)) {
              existingOrder.FamilyName.push(trimmedFamily);
            }
          }
        } else {
          ordertempname = trimmedOrder;
          FamilywithOrderArray.push({
            SpeciesOrder: trimmedOrder,
            FamilyName: [trimmedFamily],
          });
        }

        // Processing Family with Species
        if (tempname === trimmedFamily) {
          const existingFamily = SpecieswithFamilyArray.find(
            (family) => family.FamilyName === trimmedFamily
          );
          if (existingFamily) {
            existingFamily.SpeciesNames.push(trimmedScientificName);
          }
        } else {
          tempname = trimmedFamily;
          SpecieswithFamilyArray.push({
            FamilyName: trimmedFamily,
            SpeciesNames: [trimmedScientificName],
          });
        }
      });
      setOrderwithFamily(FamilywithOrderArray);
      setFamilywithSpecies(SpecieswithFamilyArray);

      setGlobalData(prevData =>
        prevData.map(module =>
          module.moduleName === "Animal Sighting"
            ? {
              ...module,
              subTabs: module.subTabs.map(subTab =>
                subTab.name === "ApprovedRecords"
                  ? {
                    ...subTab,
                    data: {
                      ...subTab.data,
                      OrderwithFamily: FamilywithOrderArray,
                      FamilywithSpecies: SpecieswithFamilyArray
                    }
                  }
                  : subTab
              )
            }
            : module
        )
      );
    }
  };


  useEffect(() => {
    if (editingRows.length === 0 && isEditing) {
      setIsEditing(!isEditing);
    }
  }, [isEditing]);

  const handleEditSelectedRows = () => {
    if (PlantsSelectedRows.length === 0 && InvertebratesSelectedRows.length === 0 && MammalsSelectedRows.length === 0 && ReptilesSelectedRows.length === 0) {
      setAlertMessage({ text: t("Noselectionoffeatures"), type: "info" });
      return;
    }
    else {
      setIsEditing(!isEditing);
      if (activeApprovedtabs.includes(t("Plants"))) {
        editingRows = PlantsSelectedRows;
        PlantscurrentRows.current.forEach((item) => {
          if (PlantsSelectedRows.includes(item.id)) {
            setCloneData({ id: item.id, data: { ...item } });
          }
        });
      }
      else if (activeApprovedtabs.includes(t("Invertebrates"))) {
        editingRows = InvertebratesSelectedRows;
        InvertebratescurrentRows.current.forEach((item) => {
          if (InvertebratesSelectedRows.includes(item.id)) {
            setCloneData({ id: item.id, data: { ...item } })
          }
        });
      }
      else if (activeApprovedtabs.includes(t("Mammals"))) {
        editingRows = MammalsSelectedRows;
        MammalscurrentRows.current.forEach((item) => {
          if (MammalsSelectedRows.includes(item.id)) {
            setCloneData({ id: item.id, data: { ...item } });
          }
        });
      }
      else if (activeApprovedtabs.includes(t("Reptiles"))) {
        editingRows = ReptilesSelectedRows;
        ReptilescurrentRows.current.forEach((item) => {
          if (ReptilesSelectedRows.includes(item.id)) {
            setCloneData({ id: item.id, data: { ...item } });
          }
        });
      }

    }
  };
  const handleRowSelectionForMobile = (rowId) => {
    if (activeApprovedtabs.includes(t("Plants"))) {
      setPlantsSelectedRows([rowId])
    }
    else if (activeApprovedtabs.includes(t("Invertebrates"))) {
      setInvertebratesSelectedRows([rowId])
    }
    else if (activeApprovedtabs.includes(t("Mammals"))) {
      setMammalsSelectedRows([rowId])
    }
    else if (activeApprovedtabs.includes(t("Reptiles"))) {
      setReptilesSelectedRows([rowId])
    }
  }
  const handleRowSelection = (rowId) => {
    if (!isEditing) {
      if (activeApprovedtabs.includes(t("Plants"))) {
        setPlantsSelectedRows((prevSelectedRows) =>
          prevSelectedRows.includes(rowId)
            ? prevSelectedRows.filter((id) => id !== rowId)
            : [...prevSelectedRows, rowId]
        );
      }
      else if (activeApprovedtabs.includes(t("Invertebrates"))) {
        setInvertebratesSelectedRows((prevSelectedRows) =>
          prevSelectedRows.includes(rowId)
            ? prevSelectedRows.filter((id) => id !== rowId)
            : [...prevSelectedRows, rowId]
        );
      }
      else if (activeApprovedtabs.includes(t("Mammals"))) {
        setMammalsSelectedRows((prevSelectedRows) =>
          prevSelectedRows.includes(rowId)
            ? prevSelectedRows.filter((id) => id !== rowId)
            : [...prevSelectedRows, rowId]
        );
      }
      else if (activeApprovedtabs.includes(t("Reptiles"))) {
        setReptilesSelectedRows((prevSelectedRows) =>
          prevSelectedRows.includes(rowId)
            ? prevSelectedRows.filter((id) => id !== rowId)
            : [...prevSelectedRows, rowId]
        );
      }
    }
    else {
      if (editingRows.includes(rowId)) {
        if (activeApprovedtabs.includes(t("Plants"))) {
          setPlantsSelectedRows((prevSelectedRows) =>
            prevSelectedRows.includes(rowId)
              ? prevSelectedRows.filter((id) => id !== rowId)
              : [...prevSelectedRows, rowId]
          );
        }
        else if (activeApprovedtabs.includes(t("Invertebrates"))) {
          setInvertebratesSelectedRows((prevSelectedRows) =>
            prevSelectedRows.includes(rowId)
              ? prevSelectedRows.filter((id) => id !== rowId)
              : [...prevSelectedRows, rowId]
          );
        }
        else if (activeApprovedtabs.includes(t("Mammals"))) {
          setMammalsSelectedRows((prevSelectedRows) =>
            prevSelectedRows.includes(rowId)
              ? prevSelectedRows.filter((id) => id !== rowId)
              : [...prevSelectedRows, rowId]
          );
        }
        else if (activeApprovedtabs.includes(t("Reptiles"))) {
          setReptilesSelectedRows((prevSelectedRows) =>
            prevSelectedRows.includes(rowId)
              ? prevSelectedRows.filter((id) => id !== rowId)
              : [...prevSelectedRows, rowId]
          );
        }
        editingRows = editingRows.slice(0, editingRows.indexOf(rowId));
        if (editingRows.length === 0) {
          setIsEditing(!isEditing);
        }
      }
    }

  };

  const onFilterClick = () => {
    setShowFilter(!showfilter);
  }
  const toggleScroll = (enable) => {
    const prContainer = document.querySelector('.pendingrecord-container');
    const prTableContainer = document.querySelector('.Animalapprovedtable');
    if (prContainer) {
      prContainer.style.overflowY = enable ? 'auto' : 'unset';
      prTableContainer.style.overflowY = enable ? 'hidden' : 'auto';
    }
  };
  const handleDateRange = () => {
    setShowDateRange((showDateRange) => {
      const newState = !showDateRange;
      toggleScroll(newState);
      return newState;
    });
  }
  const handleCloseDateRange = () => {
    setShowDateRange(false);
    toggleScroll(false);
  };
  const handleClarFilter = () => {
    setFilters({});
    setShowFilter(false);
    setIsResetBtnShow(false);
    if (originalData.length > 0) {
      if (activeApprovedtabs.includes(t("Plants"))) {
        PlantscurrentRows.current = originalData;
      }
      else if (activeApprovedtabs.includes(t("Invertebrates"))) {
        InvertebratescurrentRows.current = originalData;
      }
      else if (activeApprovedtabs.includes(t("Mammals"))) {
        MammalscurrentRows.current = originalData;
      }
      else if (activeApprovedtabs.includes(t("Reptiles"))) {
        ReptilescurrentRows.current = originalData;
      }
    }
  }

  const getTabData = (tabs) => {
    if (tabs.includes(t("Plants"))) return PlantscurrentRows.current;
    if (tabs.includes(t("Invertebrates"))) return InvertebratescurrentRows.current;
    if (tabs.includes(t("Mammals"))) return MammalscurrentRows.current;
    if (tabs.includes(t("Reptiles"))) return ReptilescurrentRows.current;
    return null;
  };
  const updateCurrentRows = (data) => {
    if (activeApprovedtabs.includes(t("Plants"))) PlantscurrentRows.current = data;
    else if (activeApprovedtabs.includes(t("Invertebrates"))) InvertebratescurrentRows.current = data;
    else if (activeApprovedtabs.includes(t("Mammals"))) MammalscurrentRows.current = data;
    else if (activeApprovedtabs.includes(t("Reptiles"))) ReptilescurrentRows.current = data;
  };

  const handleFilterChange = (key, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
    let dataToFilter = originalData.length > 0 ? originalData : getTabData(activeApprovedtabs);
    setOriginalData(dataToFilter);

    if (value === "All" || value === "") {
      resetToOriginalData(key, value);
      return;
    }

    const filterFunction = (item) => {
      const valueLower = value?.toString().toLowerCase() || "";
      if (activeApprovedtabs.includes(t("Plants"))) {
        if (
          ["LatinName", "Phenology", "GrazingSigns", "Habitat", "SpecimenCollected", "Precipitation", "PercentageCover"].includes(key)
        ) {
          const DomainValue = PlantsDomainValues[0][key === "LatinName" ? "PlantSpecies" : key]?.find(
            (name) => name.value?.toLowerCase() === valueLower
          );
          return item[key]?.toLowerCase() === (DomainValue?.value?.toLowerCase() || "");
        } else if (
          ["Location", "Recorder", "SurveyDate", "Notes", "Point_X", "Point_Y"].includes(key)
        ) {
          return item[key]?.toLowerCase().includes(valueLower);
        }
      } else if (activeApprovedtabs.includes(t("Invertebrates"))) {
        if (
          ["SpeciesOrder", "SpeciesFamily", "SpeciesName", "Habitat", "HabitatStatus", "MethodUsed", "Status"].includes(key)
        ) {
          const DomainValue = InsectsDomainValues[0][key]?.find(
            (name) => name.value?.toLowerCase() === valueLower
          );
          const keyToUse = key === "SpeciesOrder" ? "Species_Order" : key;
          return item[keyToUse]?.toLowerCase() === (DomainValue?.value?.toLowerCase() || "");
        } else if (
          ["LocationName", "Researcher", "Notes", "NumberOfIndividuals", "SpecimenNo", "PhotoNumber", "POINT_X", "POINT_Y"].includes(key)
        ) {
          return item[key]?.toLowerCase().includes(valueLower);
        }
      } else if (activeApprovedtabs.includes(t("Mammals"))) {
        if (
          ["Habitat", "SightingType", "SpeciesName"].includes(key)
        ) {
          const DomainValue = MammalsDomainValues[0][key]?.find(
            (name) => name.value?.toLowerCase() === valueLower
          );
          return item[key]?.toLowerCase() === (DomainValue?.value?.toLowerCase() || "");
        } else if (
          ["NumberofIndividuals", "LocationName", "CommonName", "LatinName", "Recorder", "SurveyDate", "Notes", "SurveyTime", "AM_PM", "Phase", "Moon_Phase", "CollectionMethod", "Activity_phase", "Point_X", "Point_Y"].includes(key)
        ) {
          return item[key]?.toLowerCase().includes(valueLower);
        }
      } else if (activeApprovedtabs.includes(t("Reptiles"))) {
        if (
          ["SpeciesName", "Habitat", "MicroHabitat", "SightingType"].includes(key)
        ) {
          const keyToUse = key === "SpeciesName" ? "Species" : key;
          const DomainValue = ReptilesDomainValues[0][keyToUse]?.find(
            (name) => name.value?.toLowerCase() === valueLower
          );
          return item[keyToUse]?.toLowerCase() === (DomainValue?.value?.toLowerCase() || "");
        } else if (
          ["NumberObserved", "LocationName", "CommonName", "LatinName", "Recorder", "ObservationDate", "Notes", "SurveyTime", "Point_X", "Point_Y"].includes(key)
        ) {
          return item[key]?.toLowerCase().includes(valueLower);
        }
      }
      return true;
    };
    //const filteredData = dataToFilter.filter((item) => { return filterFunction(item); });
    const filteredData = dataToFilter.filter(filterFunction);
    updateCurrentRows(filteredData.length > 0 ? filteredData : []);
  };

  const resetToOriginalData = (key, value) => {
    const rows = originalData.filter((item) => { return item[key]; });
    updateCurrentRows(rows.length > 0 ? rows : []);
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  };

  // useEffect(() => {
  //   if (originalData.length > 0) resetToOriginalData();
  // }, [originalData]);

  const ValidateCoordinates = (item, DisplayAttributes, Feature_Update, invalidCoord) => {
    const obj = {};
    let LongValue = "";
    let LatValue = "";
    DisplayAttributes.forEach(({ fieldname }) => {
      if (invalidCoord) return;
      const AttrName = fieldname.toLowerCase();
      if (AttrName === "objectid") {
        obj[fieldname] = parseInt(item[fieldname]);
      }
      else if (AttrName === "point_x" || AttrName === "point_y") {
        if (AttrName === "point_x") {
          LongValue = item[fieldname];
          obj[fieldname] = LongValue;
          // if ((LongValue > 51.303 && LongValue < 56.664)) {
          //   obj[fieldname] = LongValue;
          // }
          // else {
          //   showAlert(t('invalidLong'), 'error');
          //   setIsEditing(true);
          //   invalidCoord = true;
          //   return;
          // }
        }
        else if (AttrName === "point_y") {
          LatValue = item[fieldname];
          obj[fieldname] = LatValue;
          // if ((LatValue > 22.455 && LatValue < 26.326)) {
          //   obj[fieldname] = LatValue;
          // }
          // else {
          //   showAlert(t('invalidLat'), 'error');
          //   setIsEditing(true);
          //   invalidCoord = true;
          //   return;
          // }
        }
      }
      else {
        obj[fieldname] = item[fieldname];
      }
    });
    if (!invalidCoord && LongValue && LatValue) {
      const pointGeom = new Point({ longitude: LongValue, latitude: LatValue });
      const graphic_draft = new Graphic({
        geometry: pointGeom,
        attributes: obj,
      });
      Feature_Update.push(graphic_draft);
    }
  }

  const applyEdits = (currentRows, layerurl, DisplayAttributes) => {
    const layer = new FeatureLayer({
      //url: layerurl,
      url: `${layerurl}?token=` + token,
    });
    const Feature_Update = [];
    let invalidCoord = false;
    currentRows.current.forEach((item) => {
      if (invalidCoord) return;
      ValidateCoordinates(item, DisplayAttributes, Feature_Update, invalidCoord);
    });
    if (Feature_Update.length > 0) {
      handleApplyEdits(layer, { updateFeatures: Feature_Update })
    }
  }

  const handleReplaceAllField = () => {
    // if (!selectedOption) {
    //   showAlert(t("selectorenter"), "info");
    //   return;
    // }
    setLoading(true);
    let DisplayAttributes;
    if (activeApprovedtabs.includes(t("Plants"))) {
      DisplayAttributes = window.PlantInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (DisplayAttributes.includes(currentEdit.key)) {
        if (!filters[currentEdit.key]) {
          showAlert(t("filteralert", { attrName: currentEdit.key }), "error");
          setLoading(false);
          return;
        }
        else {
          if (!ApprovedPlantsDataObj[currentEdit.key]) {
            showAlert(t("selectorenter"), "error");
            setLoading(false);
            return;
          }
          else {
            for (let k = 0; k < PlantscurrentRows.current.length; k++) {
              if (currentEdit.key === "Point_X") {
                if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
                  PlantscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                }
                else {
                  showAlert(t('invalidLong'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else if (currentEdit.key === "Point_Y") {
                if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
                  PlantscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                }
                else {
                  showAlert(t('invalidLat'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else {
                PlantscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
              }
            }
            applyEdits(PlantscurrentRows, window.PlantInfo.TargetUrl, window.PlantInfo.TargetFieldMapping);
          }
        }
      }
      // for (let k = 0; k < PlantscurrentRows.current.length; k++) {
      //   PlantscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key]
      // }
      // applyEdits(PlantscurrentRows, window.PlantInfo.TargetUrl, window.PlantInfo.TargetFieldMapping);
    }
    else if (activeApprovedtabs.includes(t("Invertebrates"))) {
      DisplayAttributes = window.InvertiblesInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (!filters[currentEdit.key]) {
        showAlert(t("filteralert", { attrName: currentEdit.key }), "error");
        setLoading(false);
        return;
      }
      else {
        if (currentEdit.key === "Species_Order") {
          if (!ApprovedInsectsData.SpeciesFamily || !ApprovedInsectsData.SpeciesOrder || !ApprovedInsectsData.SpeciesName) {
            showAlert(t("selectorenter"), "error");
            setLoading(false);
            return;
          }
          else {
            for (let k = 0; k < InvertebratescurrentRows.current.length; k++) {
              InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
              InvertebratescurrentRows.current[k]["SpeciesFamily"] = CloneData.data["SpeciesFamily"]
              InvertebratescurrentRows.current[k]["SpeciesName"] = CloneData.data["SpeciesName"]
            }
            applyEdits(InvertebratescurrentRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
          }
        }
        else if (currentEdit.key === "SpeciesFamily") {
          if (!ApprovedInsectsData.SpeciesFamily || !ApprovedInsectsData.SpeciesName) {
            showAlert(t("selectorenter"), "error");
            setLoading(false);
            return;
          }
          else {
            for (let k = 0; k < InvertebratescurrentRows.current.length; k++) {
              InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
              InvertebratescurrentRows.current[k]["SpeciesName"] = CloneData.data["SpeciesName"];
            }
            applyEdits(InvertebratescurrentRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
          }
        }
        else {
          if (DisplayAttributes.includes(currentEdit.key)) {
            if (!ApprovedInsectsData[currentEdit.key]) {
              showAlert(t("selectorenter"), "error");
              setLoading(false);
              return;
            }
            else {
              for (let k = 0; k < InvertebratescurrentRows.current.length; k++) {
                if (currentEdit.key === "Point_X") {
                  if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
                    InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                  }
                  else {
                    showAlert(t('invalidLong'), 'error');
                    setLoading(false);
                    return;
                  }
                }
                else if (currentEdit.key === "Point_Y") {
                  if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
                    InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                  }
                  else {
                    showAlert(t('invalidLat'), 'error');
                    setLoading(false);
                    return;
                  }
                }
                else {
                  InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                }
              }
              applyEdits(InvertebratescurrentRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
            }
          }
        }
      }
      // if (currentEdit.key === "SpeciesOrder") {
      //   if (!insectselectedfamily || !insectselectedorder) {
      //     showAlert(t("selectorenter"), "error");
      //     return;
      //   }
      //   else {
      //     for (let k = 0; k < InvertebratescurrentRows.current.length; k++) {
      //       InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
      //       InvertebratescurrentRows.current[k]["SpeciesFamily"] = CloneData.data["SpeciesFamily"]
      //       InvertebratescurrentRows.current[k]["SpeciesName"] = CloneData.data["SpeciesName"]
      //     }
      //     applyEdits(InvertebratescurrentRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
      //   }
      // }
      // else if (currentEdit.key === "SpeciesFamily") {
      //   if (!insectselectedfamily) {
      //     showAlert(t("selectorenter"), "error");         
      //     return;
      //   }
      //   else {
      //     for (let k = 0; k < InvertebratescurrentRows.current.length; k++) {
      //       InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
      //       InvertebratescurrentRows.current[k]["SpeciesName"] = CloneData.data["SpeciesName"];
      //     }
      //     applyEdits(InvertebratescurrentRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
      //   }
      // }
      // else {
      //   for (let k = 0; k < InvertebratescurrentRows.current.length; k++) {
      //     InvertebratescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key]
      //   }
      //   applyEdits(InvertebratescurrentRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
      // }
    }
    else if (activeApprovedtabs.includes(t("Mammals"))) {
      DisplayAttributes = window.MammalsInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (DisplayAttributes.includes(currentEdit.key)) {
        if (!filters[currentEdit.key]) {
          showAlert(t("filteralert", { attrName: currentEdit.key }), "error");
          setLoading(false);
          return;
        }
        else {
          if (!MammalsDataObj[currentEdit.key]) {
            showAlert(t("selectorenter"), "error");
            setLoading(false);
            return;
          }
          else {
            for (let k = 0; k < MammalscurrentRows.current.length; k++) {
              if (currentEdit.key === "Point_X") {
                if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
                  MammalscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                }
                else {
                  showAlert(t('invalidLong'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else if (currentEdit.key === "Point_Y") {
                if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
                  MammalscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                }
                else {
                  showAlert(t('invalidLat'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else {
                MammalscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
              }
            }
            applyEdits(MammalscurrentRows, window.MammalsInfo.TargetUrl, window.MammalsInfo.TargetFieldMapping);
          }
        }
      }
      // for (let k = 0; k < MammalscurrentRows.current.length; k++) {
      //   MammalscurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key]
      // }
      // applyEdits(MammalscurrentRows, window.MammalsInfo.TargetUrl, window.MammalsInfo.TargetFieldMapping);
    }
    else if (activeApprovedtabs.includes(t("Reptiles"))) {
      DisplayAttributes = window.ReptilesInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (DisplayAttributes.includes(currentEdit.key)) {
        if (!filters[currentEdit.key]) {
          showAlert(t("filteralert", { attrName: currentEdit.key }), "error");
          setLoading(false);
          return;
        }
        else {
          if (!ReptilesDataObj[currentEdit.key]) {
            showAlert(t("selectorenter"), "error");
            setLoading(false);
            return;
          }
          else {
            for (let k = 0; k < ReptilescurrentRows.current.length; k++) {
              if (currentEdit.key === "Point_X") {
                if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
                  ReptilescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                }
                else {
                  showAlert(t('invalidLong'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else if (currentEdit.key === "Point_Y") {
                if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
                  ReptilescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
                }
                else {
                  showAlert(t('invalidLat'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else {
                ReptilescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key];
              }
            }
            applyEdits(ReptilescurrentRows, window.ReptilesInfo.TargetUrl, window.ReptilesInfo.TargetFieldMapping);
          }
        }
      }
      // for (let k = 0; k < ReptilescurrentRows.current.length; k++) {
      //   ReptilescurrentRows.current[k][currentEdit.key] = CloneData.data[currentEdit.key]
      // }
      // applyEdits(ReptilescurrentRows, window.ReptilesInfo.TargetUrl, window.ReptilesInfo.TargetFieldMapping);
    }
    setModalVisible(false);
  };

  const handleButtonClick = (buttonId, onClickFunction) => {
    setActiveButton((prevActiveButton) =>
      prevActiveButton === buttonId ? null : buttonId
    );
    onClickFunction();
  };

  const applyEditsForReplaceField = (currentSelectedRow, layerurl, DisplayAttributes) => {
    const Feature_Update = [];
    let invalidCoord = false;
    const layer = new FeatureLayer({
      //url: layerurl,
      url: `${layerurl}?token=` + token,
    });
    if (currentSelectedRow.includes(CloneData.id)) {
      const item = CloneData.data;
      ValidateCoordinates(item, DisplayAttributes, Feature_Update, invalidCoord);
      if (Feature_Update.length > 0) {
        handleApplyEdits(layer, { updateFeatures: Feature_Update })
      }
    }
  }

  const handleReplaceField = () => {
    let DisplayAttributes;
    // if (!selectedOption) {
    //   showAlert(t("selectorenter"), "info");
    //   return;
    // }
    setLoading(true);
    if (activeApprovedtabs.includes(t("Plants"))) {
      DisplayAttributes = window.PlantInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (DisplayAttributes.includes(currentEdit.key)) {
        if (!ApprovedPlantsDataObj[currentEdit.key]) {
          showAlert(t("selectorenter"), "error");
          setLoading(false);
          return;
        }
        else {
          const recordIndex = PlantscurrentRows.current.findIndex(row => row.id === currentEdit.rowId);
          if (currentEdit.key === "Point_X") {
            if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
              PlantscurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
              applyEditsForReplaceField(PlantsSelectedRows, window.PlantInfo.TargetUrl, window.PlantInfo.TargetFieldMapping);
            }
            else {
              showAlert(t('invalidLong'), 'error');
              setLoading(false);
              return;
            }
          }
          else if (currentEdit.key === "Point_Y") {
            if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
              PlantscurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
              applyEditsForReplaceField(PlantsSelectedRows, window.PlantInfo.TargetUrl, window.PlantInfo.TargetFieldMapping);
            }
            else {
              showAlert(t('invalidLat'), 'error');
              setLoading(false);
              return;
            }
          }
          else {
            applyEditsForReplaceField(PlantsSelectedRows, window.PlantInfo.TargetUrl, window.PlantInfo.TargetFieldMapping);
          }
        }
      }
      else {
        applyEditsForReplaceField(PlantsSelectedRows, window.PlantInfo.TargetUrl, window.PlantInfo.TargetFieldMapping);
      }
    }
    else if (activeApprovedtabs.includes(t("Invertebrates"))) {
      DisplayAttributes = window.InvertiblesInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (DisplayAttributes.includes(currentEdit.key)) {
        if (currentEdit.key === "Species_Order") {
          if (!ApprovedInsectsData.SpeciesFamily || !ApprovedInsectsData.SpeciesOrder || !ApprovedInsectsData.SpeciesName) {
            showAlert(t("selectorenter"), "error");
            setLoading(false);
            return;
          }
          else {
            applyEditsForReplaceField(InvertebratesSelectedRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
          }
        }
        else if (currentEdit.key === "SpeciesFamily") {
          if (!ApprovedInsectsData.SpeciesFamily || !ApprovedInsectsData.SpeciesName) {
            showAlert(t("selectorenter"), "error");
            setLoading(false);
            return;
          }
          else {
            applyEditsForReplaceField(InvertebratesSelectedRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
          }
        }
        else {
          if (DisplayAttributes.includes(currentEdit.key)) {
            if (!ApprovedInsectsData[currentEdit.key]) {
              showAlert(t("selectorenter"), "error");
              setLoading(false);
              return;
            }
            else {
              const recordIndex = InvertebratescurrentRows.current.findIndex(row => row.id === currentEdit.rowId);
              if (currentEdit.key === "Point_X") {
                if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
                  InvertebratescurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
                  applyEditsForReplaceField(InvertebratesSelectedRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
                }
                else {
                  showAlert(t('invalidLong'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else if (currentEdit.key === "Point_Y") {
                if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
                  InvertebratescurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
                  applyEditsForReplaceField(InvertebratesSelectedRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
                }
                else {
                  showAlert(t('invalidLat'), 'error');
                  setLoading(false);
                  return;
                }
              }
              else {
                applyEditsForReplaceField(InvertebratesSelectedRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
              }
            }
          }
          //applyEditsForReplaceField(InvertebratesSelectedRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
        }
      }
      else {
        applyEditsForReplaceField(InvertebratesSelectedRows, window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
      }
    }
    else if (activeApprovedtabs.includes(t("Mammals"))) {
      DisplayAttributes = window.MammalsInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (DisplayAttributes.includes(currentEdit.key)) {
        if (!MammalsDataObj[currentEdit.key]) {
          showAlert(t("selectorenter"), "error");
          setLoading(false);
          return;
        }
        else {
          const recordIndex = MammalscurrentRows.current.findIndex(row => row.id === currentEdit.rowId);
          if (currentEdit.key === "Point_X") {
            if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
              MammalscurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
              applyEditsForReplaceField(MammalsSelectedRows, window.MammalsInfo.TargetUrl, window.MammalsInfo.TargetFieldMapping);
            }
            else {
              showAlert(t('invalidLong'), 'error');
              setLoading(false);
              return;
            }
          }
          else if (currentEdit.key === "Point_Y") {
            if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
              MammalscurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
              applyEditsForReplaceField(MammalsSelectedRows, window.MammalsInfo.TargetUrl, window.MammalsInfo.TargetFieldMapping);
            }
            else {
              showAlert(t('invalidLat'), 'error');
              setLoading(false);
              return;
            }
          }
          else {
            applyEditsForReplaceField(MammalsSelectedRows, window.MammalsInfo.TargetUrl, window.MammalsInfo.TargetFieldMapping);
          }
        }
      }
      else {
        applyEditsForReplaceField(MammalsSelectedRows, window.MammalsInfo.TargetUrl, window.MammalsInfo.TargetFieldMapping);
      }
    }
    else if (activeApprovedtabs.includes(t("Reptiles"))) {
      DisplayAttributes = window.ReptilesInfo.TargetFieldMapping.filter(item => item.Mandatory === true).map(item => item.fieldname);
      if (DisplayAttributes.includes(currentEdit.key)) {
        if (!ReptilesDataObj[currentEdit.key]) {
          showAlert(t("selectorenter"), "error");
          setLoading(false);
          return;
        }
        else {
          const recordIndex = ReptilescurrentRows.current.findIndex(row => row.id === currentEdit.rowId);
          if (currentEdit.key === "Point_X") {
            if ((CloneData.data[currentEdit.key] > 51.303 && CloneData.data[currentEdit.key] < 56.664)) {
              ReptilescurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
              applyEditsForReplaceField(ReptilesSelectedRows, window.ReptilesInfo.TargetUrl, window.ReptilesInfo.TargetFieldMapping);
            }
            else {
              showAlert(t('invalidLong'), 'error');
              setLoading(false);
              return;
            }
          }
          else if (currentEdit.key === "Point_Y") {
            if ((CloneData.data[currentEdit.key] > 22.455 && CloneData.data[currentEdit.key] < 26.326)) {
              ReptilescurrentRows.current[recordIndex][currentEdit.key] = CloneData.data[currentEdit.key];
              applyEditsForReplaceField(ReptilesSelectedRows, window.ReptilesInfo.TargetUrl, window.ReptilesInfo.TargetFieldMapping);
            }
            else {
              showAlert(t('invalidLat'), 'error');
              setLoading(false);
              return;
            }
          }
          else {
            applyEditsForReplaceField(ReptilesSelectedRows, window.ReptilesInfo.TargetUrl, window.ReptilesInfo.TargetFieldMapping);
          }
        }
      }
      else {
        applyEditsForReplaceField(ReptilesSelectedRows, window.ReptilesInfo.TargetUrl, window.ReptilesInfo.TargetFieldMapping);
      }
    }
    setModalVisible(false);
  };

  const handleFieldClick = (rowId, key, type) => {
    setCurrentEdit({ rowId, key, type });
    setModalVisible(true);
  };
  const handleCanceledting = () => {
    setIsEditing(!isEditing);
    editingRows = [];
    setInvertebratesSelectedRows([]);
    setPlantsSelectedRows([]);
    setMammalsSelectedRows([]);
    setReptilesSelectedRows([]);
    clearControls();
    setModalVisible(false);
    setSelectedOption(null);
    setIsButtonDisabled(false);
    setDateFilters({
      SurveyFromDate: "",
      SurveyToDate: ""
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { SurveyFromDate, SurveyToDate } = datefilters;
        const tabMapping = {
          [t("Invertebrates")]: {
            layerInfo: window.InvertiblesInfo,
          },
          [t("Plants")]: {
            layerInfo: window.PlantInfo,
          },
          [t("Mammals")]: {
            layerInfo: window.MammalsInfo,
          },
          [t("Reptiles")]: {
            layerInfo: window.ReptilesInfo,
          },
        };
        const { layerInfo } = tabMapping[activeApprovedtabs];
        await fetchApprovedRecordsByType(activeApprovedtabs, layerInfo.TargetUrl, layerInfo.TargetFieldMapping);
      }
      catch (ex) {
        console.log("Error fetching approved records:", ex);
      } finally {
        setLoading(false);
      }
    }
    if (isButtonDisabled === false) {
      fetchData();
    }
  }, [datefilters])

  const handleCancelClick = () => {
    setModalVisible(false);
    setApprovedInsectsData(initialInsectState);
    setApprovedPlantsDataObj(initialPlantState);
    setMammalsDataObj(initialMammalsState);
    setReptilesDataObj(initialReptilesState);
  }

  const GetFamilyDropdown = (value) => {
    const matchingOrder = speciesOrder?.find((order) => order.key === value || order.value === value);
    setDropdownFamilyValues([]);
    setDropdownSpeciesValues([]);
    if (matchingOrder) {
      setInsectsSelectedOrder(matchingOrder.value);
      setApprovedInsectsData((prevState) => ({
        ...prevState,
        Species_Order: { value: matchingOrder.key, label: matchingOrder.value }, // Correct format for react-select
        SpeciesOrder: parseInt(matchingOrder.key),
      }));
      const newFamilyNames = OrderFamilyNames.filter((item) => item.SpeciesOrder === matchingOrder.value)
        .map((item) => item.FamilyNames)
        .flat();  // Flatten the array if multiple family names per order
      setDropdownFamilyValues(newFamilyNames);

      setCloneData((prevCloneData) => ({
        ...prevCloneData,
        data: {
          ...prevCloneData.data,
          Species_Order: matchingOrder.value,
          SpeciesOrder: parseInt(matchingOrder.key),
        }
      }));
    }
  };
  const GetSpeciesDropdown = (value) => {
    const matchingFamily = familyName?.find((family) => family.value === value);
    setDropdownSpeciesValues([]);
    if (matchingFamily) {
      setInsectsSelectedFamily(matchingFamily.value);
      setApprovedInsectsData((prevState) => ({
        ...prevState,
        SpeciesFamily: { value: matchingFamily.key, label: matchingFamily.value }, // Correct format for react-select
      }));
      const newSpeciesNames = FamilySpeciesNames.filter((item) => item.FamilyName === matchingFamily.value)
        .map((item) => item.SpeciesNames)
        .flat();  // Flatten the array if multiple family names per order
      setDropdownSpeciesValues(newSpeciesNames);

      setCloneData((prevCloneData) => ({
        ...prevCloneData,
        data: {
          ...prevCloneData.data,
          SpeciesFamily: matchingFamily.value,
        }
      }));
    }
  };
  const handleInsectInputChange = (e, name) => {
    let value;
    if (e && e.target) {
      value = e.target.value;
    } else if (e && e.value) {
      value = e.value;
    }
    setSelectedOption(value);
    setCloneData((prevCloneData) => ({
      ...prevCloneData,
      data: {
        ...prevCloneData.data,
        [name]: value,
      }
    }));
    if (activeApprovedtabs.includes(t("Plants"))) {
      setApprovedPlantsDataObj((prevState) => ({
        ...prevState,
        [name]: value, // Update the correct field using the name
      }));
    }
    else if (activeApprovedtabs.includes(t("Invertebrates"))) {
      setApprovedInsectsData((prevState) => ({
        ...prevState,
        [name]: value, // Update the correct field using the name
      }));
    }
    else if (activeApprovedtabs.includes(t("Mammals"))) {
      setMammalsDataObj((prevState) => ({
        ...prevState,
        [name]: value, // Update the correct field using the name
      }));
    }
    else if (activeApprovedtabs.includes(t("Reptiles"))) {
      setReptilesDataObj((prevState) => ({
        ...prevState,
        [name]: value, // Update the correct field using the name
      }));
    }
  };

  const handleApplyEdits = async (layer, options) => {
    if (!layer) return;
    try {
      const result = await layer.applyEdits(options);
      const editsApplied = result.updateFeatureResults.concat(result.addFeatureResults, result.deleteFeatureResults);
      if (editsApplied && editsApplied.length > 0) {
        showAlert(t('updatefeature'), "success");
        setIsDataModified(true);
        setIsEditing(!isEditing);
        setActiveApprovedtab(activeApprovedtabs);
        if (activeApprovedtabs.includes(t("Plants"))) {
          setPlantsSelectedRows([]);
          fetchApprovedRecordsByType(t("Plants"), window.PlantInfo.TargetUrl, window.PlantInfo.TargetFieldMapping);
        }
        else if (activeApprovedtabs.includes(t("Invertebrates"))) {
          setInvertebratesSelectedRows([]);
          fetchApprovedRecordsByType(t("Invertebrates"), window.InvertiblesInfo.TargetUrl, window.InvertiblesInfo.TargetFieldMapping);
          setInsectsSelectedOrder(null);
          setInsectsSelectedFamily(null);
        }
        else if (activeApprovedtabs.includes(t("Mammals"))) {
          setMammalsSelectedRows([]);
          fetchApprovedRecordsByType(t("Mammals"), window.MammalsInfo.TargetUrl, window.MammalsInfo.TargetFieldMapping);
        }
        else if (activeApprovedtabs.includes(t("Reptiles"))) {
          setReptilesSelectedRows([]);
          fetchApprovedRecordsByType(t("Reptiles"), window.ReptilesInfo.TargetUrl, window.ReptilesInfo.TargetFieldMapping);
        }
        setSelectedOption(null);
        handleClarFilter();
        clearControls();
        setLoading(false);
      } else {
        showAlert(t("unableupdatefeature"), "error");
        setLoading(false);
        setIsDataModified(false)
      }
    } catch (error) {
      let message = t('errorapplyedits');
      if (error.code && error.code === 400) {
        message = t('insufficientPortalPermission');
      }
      setLoading(false);
      showAlert(message, "error");
    }
  };

  const clearControls = () => {
    setApprovedInsectsData(initialInsectState);
    setApprovedPlantsDataObj(initialPlantState);
    setMammalsDataObj(initialMammalsState);
    setReptilesDataObj(initialReptilesState);
  }
  const bindDropDownValues = (items) => {
    return items.map((item) => ({
      value: item.key,
      label: item.value,
    }));
  }
  useEffect(() => {
    if (activeApprovedtabs.includes(t("Invertebrates")) && InsectsDomainValues.length > 0 && OrderwithFamily.length > 0 && FamilywithSpecies.length > 0 && currentEdit && InvertebratescurrentRows.current) {
      const currentRow = InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId);
      // if (currentEdit.key === "SpeciesFamily") {
      //   OrderwithFamily.forEach((name) => {
      //     if (name.SpeciesOrder === InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.['Species_Order']) {
      //       const FamilyArray = name.FamilyName.map(family => ({ key: family, value: family }));
      //       setDropdownValues(bindDropDownValues(FamilyArray));
      //     }
      //   });
      // }
      // if (currentEdit.key === "SpeciesName") {
      //   FamilywithSpecies.forEach((name) => {
      //     if (name.FamilyName === InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.['SpeciesFamily']) {
      //       const SpeciesArray = name.SpeciesNames.map(species => ({ key: species, value: species }));
      //       setDropdownValues(bindDropDownValues(SpeciesArray));
      //     }
      //   });
      // }
      if (currentEdit.key === "SpeciesFamily" && currentRow?.["Species_Order"]) {
        const match = OrderwithFamily.find(name => name.SpeciesOrder === currentRow["Species_Order"]);
        if (match) {
          const FamilyArray = match.FamilyName.map(family => ({ key: family, value: family }));
          setDropdownValues(bindDropDownValues(FamilyArray));
        }
      }

      if (currentEdit.key === "SpeciesName" && currentRow?.["SpeciesFamily"]) {
        const match = FamilywithSpecies.find(name => name.FamilyName === currentRow["SpeciesFamily"]);
        if (match) {
          const SpeciesArray = match.SpeciesNames.map(species => ({ key: species, value: species }));
          setDropdownValues(bindDropDownValues(SpeciesArray));
        }
      }
      if (currentEdit.key === "Habitat" || currentEdit.key === "HabitatStatus" || currentEdit.key === "MethodUsed" || currentEdit.key === "Status") {
        setDropdownValues(bindDropDownValues(InsectsDomainValues[0][currentEdit.key]));
      }
    }
  }, [currentEdit, InvertebratescurrentRows, OrderwithFamily, FamilywithSpecies, InsectsDomainValues, activeApprovedtabs]);
  useEffect(() => {
    if (activeApprovedtabs.includes(t("Plants")) && PlantsDomainValues.length > 0) {
      if (currentEdit.key === "LatinName" || currentEdit.key === "Habitat" || currentEdit.key === "Phenology" || currentEdit.key === "GrazingSigns" || currentEdit.key === "SpecimenCollected" || currentEdit.key === "Precipitation" || currentEdit.key === "PercentageCover") {
        if (currentEdit.key === "LatinName") {
          setPlantsDropdownValues(bindDropDownValues(PlantsDomainValues[0]['PlantSpecies']));
        }
        else {
          setPlantsDropdownValues(bindDropDownValues(PlantsDomainValues[0][currentEdit.key]));
        }
      }
    }
  }, [currentEdit, PlantscurrentRows, PlantsDomainValues, activeApprovedtabs]);

  useEffect(() => {
    if (activeApprovedtabs.includes(t("Mammals")) && MammalsDomainValues.length > 0) {
      if (currentEdit.key === "SpeciesName" || currentEdit.key === "Habitat" || currentEdit.key === "SightingType") {
        setMammalsDropdownValues(bindDropDownValues(MammalsDomainValues[0][currentEdit.key]));
      }
    }
  }, [currentEdit, MammalscurrentRows, MammalsDomainValues, activeApprovedtabs]);
  useEffect(() => {
    if (activeApprovedtabs.includes(t("Reptiles")) && ReptilesDomainValues.length > 0) {
      if (currentEdit.key === "SpeciesName" || currentEdit.key === "Habitat" || currentEdit.key === "MicroHabitat" || currentEdit.key === "SightingType") {
        const keyToUse = currentEdit.key === "SpeciesName" ? "Species" : currentEdit.key;
        setReptilesDropdownValues(bindDropDownValues(ReptilesDomainValues[0][keyToUse]));
        // setReptilesDropdownValues(bindDropDownValues(ReptilesDomainValues[0][currentEdit.key]));
      }
    }
  }, [currentEdit, ReptilescurrentRows, ReptilesDomainValues, activeApprovedtabs]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    if (name === "SurveyFromDate" || name === "SurveyToDate") {
      const today = new Date().toISOString().split("T")[0] + "T" + new Date().toTimeString().slice(0, 5);
      if (value > today) {
        showAlert(t("Future dates are not allowed."), "error");
        setDateFilters((prevData) => ({
          ...prevData,
          [name]: ""
        }));
        return;
      }
    }
    setDateFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [name]: value,
      };
      const isValid = updatedFilters.SurveyFromDate && updatedFilters.SurveyToDate;
      setIsButtonDisabled(!isValid); // Disable button if either date is missing
      return updatedFilters;
    });
  };
  const handleSearchDatafordates = () => {
    const tabMapping = {
      [t("Invertebrates")]: {
        layerInfo: window.InvertiblesInfo,
      },
      [t("Plants")]: {
        layerInfo: window.PlantInfo,
      },
      [t("Mammals")]: {
        layerInfo: window.MammalsInfo,
      },
      [t("Reptiles")]: {
        layerInfo: window.ReptilesInfo,
      },
    };
    const { layerInfo } = tabMapping[activeApprovedtabs];
    if (datefilters.SurveyToDate != "" && datefilters.SurveyFromDate != "") {
      fetchApprovedRecordsByType(activeApprovedtabs, layerInfo.TargetUrl, layerInfo.TargetFieldMapping);
    }
    else {
      showAlert(t('pleaseSelectDateRange'), "error");
    }
  };

  const handleTabChange = (param) => {
    setActiveApprovedtab(param);
    setIsDataModified(false)
    setDateFilters({
      SurveyFromDate: "",
      SurveyToDate: ""
    });
    setOriginalData([]);
    setIsButtonDisabled(true);
    setShowFilter(false);
  };

  const sortTable = (columnName, tableId) => {
    const table = document.getElementById(tableId);
    const rows = Array.from(table.querySelectorAll("tbody tr"));
    const isAscending = table.getAttribute("data-sort-asc") === "true";
    const headers = Array.from(table.querySelectorAll("thead tr:nth-child(1) th"));
    let headerIndex = headers.findIndex(th => th.innerText.trim() === columnName);
    if (headerIndex === -1) return; // Exit if column not found
    let actualColumnIndex = -1;
    rows.some(row => {
      let visibleIndex = -1;
      Array.from(row.cells).forEach((td, i) => {
        if (td.style.display !== "none") {
          visibleIndex++;
        }
        if (visibleIndex === headerIndex) {
          actualColumnIndex = i;
          return true; // Stop iteration once we find the correct index
        }
      });
      return actualColumnIndex !== -1; // Exit loop once we get the correct index
    });
    if (actualColumnIndex === -1) return; // Ensure we found a valid index
    const sortedRows = rows.sort((rowA, rowB) => {
      let cellA = rowA.cells[actualColumnIndex]?.innerText.trim().toLowerCase() || "";
      let cellB = rowB.cells[actualColumnIndex]?.innerText.trim().toLowerCase() || "";

      // Sanitize cell values before comparing
      cellA = DOMPurify.sanitize(cellA);
      cellB = DOMPurify.sanitize(cellB);

      return isAscending ? cellA.localeCompare(cellB) : cellB.localeCompare(cellA);
    });
    rows.forEach(row => table.tBodies[0].removeChild(row));
    sortedRows.forEach(row => table.tBodies[0].appendChild(row));
    table.setAttribute("data-sort-asc", !isAscending);
  };

  useEffect(() => {
    const updateView = () => setIsMobileView(window.innerWidth <= 767);
    updateView();
    window.addEventListener("resize", updateView);

    // const _portalToken = getLocalStorageItem("PortalToken");
    // if(_portalToken){
    //   registerMapServiceToken(_portalToken);
    // }

    return () => window.removeEventListener("resize", updateView);
  }, []);

  const handleMobFilterChange = (key, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  };
  const handleMobFilterReset = () => {
    resetMobFilters()
  };

  const resetMobFilters = () => {
    updateCurrentRows(originalData);
    setFilters({});
  };

  const handleFilterApply = () => {
    if (Object.keys(filters).length > 0) {
      applyFilters();
    }
  }

  const applyFilters = () => {
    let dataToFilter =
      originalData.length > 0 ? originalData : getTabData(activeApprovedtabs);
    setOriginalData(dataToFilter);
    const tabDataMapping = {
      [t("Plants")]: {
        currentRows: PlantscurrentRows,
        domainValues: PlantsDomainValues[0],
      },
      [t("Invertebrates")]: {
        currentRows: InvertebratescurrentRows,
        domainValues: InsectsDomainValues[0],
      },
      [t("Mammals")]: {
        currentRows: MammalscurrentRows,
        domainValues: MammalsDomainValues[0],
      },
      [t("Reptiles")]: {
        currentRows: ReptilescurrentRows,
        domainValues: ReptilesDomainValues[0],
      },
    };
    const { domainValues } = tabDataMapping[activeApprovedtabs];
    let filteredData = dataToFilter.filter((item) => {
      return Object.keys(filters).every((key) => {
        const value = filters[key];
        if (value === "All" || value === "") {
          return true; // Do not filter for this key
        }
        return mobFilterRows(item, domainValues, key, value);
      });
    });
    if (filteredData.length == 0) {
      setIsResetBtnShow(true)
    }
    else {
      setIsResetBtnShow(false)
    }
    updateCurrentRows(filteredData.length > 0 ? filteredData : []);
  };
  const mobFilterRows = (item, domainValues, key, value) => {
    const valueLower = value.toLowerCase();
    if (domainValues[key]) {
      const DomainValue = domainValues[key]?.find(
        (name) => name.value.toLowerCase() === valueLower
      );
      //const keyToUse = key === "SpeciesOrder" ? "Species_Order" : key;
      const keyToUse = key === "PlantSpecies"
        ? "LatinName"
        : key === "SpeciesOrder"
          ? "Species_Order"
          : key === "Species"
            ? "SpeciesName"
            : key;
      return item[keyToUse]?.toLowerCase() === DomainValue?.value.toLowerCase();
    }
    else if (typeof item[key] === "string") {
      return item[key]?.toLowerCase().includes(value.toLowerCase());
    } else if (typeof item[key] === "number") {
      return item[key].toString().includes(value);
    }
    return true;
  };

  const [mobFilterShow, setMobFilterShow] = useState(false);

  const handleMobFilterClose = () => setMobFilterShow(false);
  const handleMobFilterShow = () => setMobFilterShow(true);
  const commonProps = {
    formatToDatetimeLocal,
    handleInsectInputChange,
    handleReplaceField,
    handleRowSelectionForMobile,
    handleFieldClick,
    handleEditSelectedRows,
  }

  const useIsMobile = (breakpoint = 768) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= breakpoint);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);

    return isMobile;
  };

  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      if (fullView) {
        const fullContainer = document.querySelector('body');
        setWidgetMobEl(fullContainer);
      } else {
        const mobEl = document.getElementById('widgetMob');
        setWidgetMobEl(mobEl);
      }
    }
  }, [isMobile, fullView]);

  return (
    <>
      {loading && <DivLoader />}
      <div id="" className="animal-approved-container h-100">
        <AlertMessage message={alertmessage} closeMessage={() => setAlertMessage(null)}
        />
        {scriptLoaded ? (
          <>
            <Tabs className="animTab" selectedTabClassName='pollutantsfilterTab' selectedIndex={filteredApprovedList.indexOf(activeApprovedtabs)}
              forceRenderTabPanel>
              <div className="row">
                <div className='col-12 ani-pending'>
                  <TabList className="animalSubTab">
                    {filteredApprovedList.map((param) => (
                      <Tab key={param} onClick={() => handleTabChange(param)}>{
                        `${param} (${
                          param === t("Plants")
                            ? (PlantsCount || PlantsData.length)
                            : param === t("Invertebrates")
                              ? (InvertebratesCount || InvertebratesData.length)
                              : param === t("Mammals")
                                ? (MammalsCount || MammalsData.length)
                                : (ReptilesCount || ReptilesData.length)
                        })`
                      }</Tab>
                    ))}
                  </TabList>
                </div>
              </div>
              {groupNames.includes(window.userGroups.invertebrates) && (
                <TabPanel>
                  <div className="approvedrecord-container pendingrecord-container">
                    <div className="animalFilterGrid animalFilterGrid--approved">
                      <h6 className="mb-3 mt-1 me-2" style={{ fontSize: '0.8rem' }}>{t('approveInsects')}</h6>
                      <div className="gap5 filterDateBlk">
                        <div className="animalFilterBlk--approved onshiftGaping">
                          <button className={`btn btn-outline approvedrecord-button ${showfilter ? "active" : ""}`}
                            onClick={isMobileView ? handleMobFilterShow : onFilterClick}
                            data-tooltip-content={t('filter')}
                            data-tooltip-id={`tooltip-filter`}
                            data-tooltip-place="bottom"
                          >
                            <span className="filter-text" style={{ color: theme === "dark" ? "#fff" : showfilter ? "#179075" : "#000" }}
                              ref={(el) => { if (el) elementInstances["span1"] = el; }}
                            >{t('filter')}</span>
                            <BiFilterAlt className="filter-icon" size={15} />
                          </button>
                          {/* <Tooltip id={`tooltip-filter`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                          {showfilter && (
                            <button className="btn clearfilter-btn" onClick={handleClarFilter}>
                              <span>{t('reset')}</span>
                              <ImCancelCircle size={15} />
                            </button>
                          )}
                        </div>
                        <button
                          className={`btn btn-outline btn-dateRange ${showDateRange ? "active" : ""}`}
                          onClick={handleDateRange}
                          data-tooltip-content={t("Date Range")}
                          data-tooltip-id={`tooltip-dateRange`}
                          data-tooltip-place="bottom"
                        >
                          <span>{t('date')}</span>
                          <RxCalendar size={15} />
                        </button>
                        {/* <Tooltip id={`tooltip-dateRange`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                        {(showDateRange || (fullView === "fullView" && !isMobileView)) && (
                          <div className="dateRangeBlk">
                            <div className="dateRangeHead d-flex justify-content-between aign-items-center">
                              <h6>{t('datesrange')}</h6>
                              <button className="crossBtn border-0" onClick={handleCloseDateRange}>
                                <RxCross2 />
                              </button>

                            </div>
                            <div className="row dateRandeInputWrp">
                              <div className="col-md-6 mb-1">
                                <label>{t('fromDate')}:</label>
                                <input className="form-control" type="date" name="SurveyFromDate" value={datefilters.SurveyFromDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                              </div>
                              <div className="col-md-6 mb-1">
                                <label>{t('toDate')}:</label>
                                <input className="form-control" type="date" name="SurveyToDate" value={datefilters.SurveyToDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                              </div>
                            </div>
                            {/* <div className="d-flex justify-content-end mt-3 dateRangeFooter">
           <button className="btn btn-primary" onClick={handleSearchDatafordates} disabled={isButtonDisabled}>{t('submit')}</button>
         </div> */}

                          </div>
                        )}
                      </div>
                      <div className="gap5">
                        <button
                          className={`btn btn-outline ${(activeButton === "edit" && isEditing) ? "active" : ""}`}
                          onClick={() => handleButtonClick("edit", handleEditSelectedRows)}
                          data-tooltip-content={t("edit")}
                          data-tooltip-id={`tooltip-edit`}
                          data-tooltip-place="bottom"
                        >

                          <span>{t('edit')}</span>
                          <FiEdit2 size={15} />
                        </button>
                        {/* <Tooltip id={`tooltip-edit`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                        <button className="btn btn-outline cancel" onClick={handleCanceledting}>
                          <span>{t('cancel')}</span>
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive Animalapprovedtable">
                      <div className="TableScrollDiv desktopOnly">
                        <table id="tblApprovedInsects" className="table textwrap" data-sort-asc="true">
                          <thead className="thead-dark">
                            <tr>
                              <th>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={
                                    InvertebratesSelectedRows.length > 0 &&
                                    InvertebratesSelectedRows.length === (InvertebratescurrentRows?.current || []).length
                                  }
                                  disabled={isEditing && editingRows.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const allRowIds = (InvertebratescurrentRows?.current || []).map(
                                        (row) => row.id // Assuming `id` is the unique identifier
                                      );
                                      setInvertebratesSelectedRows(allRowIds);
                                    } else {
                                      setInvertebratesSelectedRows([]);
                                    }
                                  }}
                                />
                              </th>
                              {invertebrateheaders.map((key) => (
                                <th key={key} onClick={() => sortTable(key, "tblApprovedInsects")}>{key}</th>
                              ))}
                            </tr>
                            {showfilter && (
                              // <tr>
                              //   <th></th>
                              //   {InvertebratescurrentRows && InvertebratescurrentRows.current.length > 0 && InvertebratescurrentRows.current[0] ? (
                              //     Object.keys(InvertebratescurrentRows.current[0]).filter((key) => key !== "OBJECTID" && key != "id").map((key) => {
                              //       const filteredAttributes = window.InvertiblesInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'ObservationDate' && attr.fieldname !== 'OBJECTID' && attr.fieldname === key);
                              //       return (
                              //         <th key={`${key}-filter`}>
                              //           {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (

                              //             <Select
                              //               options={[
                              //                 { value: "All", label: "All" },
                              //                 ...(InsectsDomainValues[0]?.[key]?.map((domain) => ({
                              //                   value: domain.key,
                              //                   label: domain.value,
                              //                 })) || []),
                              //               ]}
                              //               value={
                              //                 filters[key]
                              //                   ? {
                              //                     value: filters[key],
                              //                     label: filters[key]
                              //                   }
                              //                   : { value: "All", label: "All" }
                              //               }
                              //               onChange={(selectedOption) => handleFilterChange(key, selectedOption?.label || "All")}
                              //               components={{
                              //                 DropdownIndicator: CustomDropdownIndicator,
                              //               }}
                              //               className={`react-select-container`}
                              //               classNamePrefix="react-select"
                              //               placeholder={t("selectvalue", { attrName: key })}
                              //             //placeholder={t(`Select ${key}`)}
                              //             />
                              //           ) : (
                              //             <input className="form-control" type="text" value={filters[key] || ''} onChange={(e) => handleFilterChange(key, e.target.value)} />
                              //           )}
                              //         </th>
                              //       );
                              //     })
                              //   ) : (
                              //     <td colSpan={Object.keys(InvertebratescurrentRows.current[0] || {}).length}>
                              //       {t('No data available')}
                              //     </td>
                              //   )}
                              // </tr>
                              <tr>
                                <th></th>
                                {(
                                  InvertebratescurrentRows.current.length > 0
                                    ? Object.keys(InvertebratescurrentRows.current[0]) // Use keys from the data if rows exist
                                    : window.InvertiblesInfo.TargetFieldMapping.map((attr) => attr.fieldname) // Fallback to TargetFieldMapping keys
                                )
                                  .filter((key) => key !== "OBJECTID" && key !== "id" && key !== "Status" && key !== "FDC_Global_ID" && key !== "SpeciesOrder") // Filter out unwanted keys
                                  .map((key) => {
                                    const filteredAttributes = window.InvertiblesInfo.TargetFieldMapping.filter(
                                      (attr) =>
                                        attr.fieldname !== "id" &&
                                        attr.fieldname !== "ObservationDate" &&
                                        attr.fieldname !== "OBJECTID" &&
                                        attr.fieldname !== "SpeciesOrder" &&
                                        attr.fieldname === key
                                    );
                                    const keyToUse = key === "Species_Order" ? "SpeciesOrder" : key;
                                    return (
                                      <th key={`${keyToUse}-filter`}>
                                        {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (
                                          // Render a Select dropdown for fields with Domains
                                          <Select
                                            options={[
                                              { value: "All", label: "All" },
                                              ...(InsectsDomainValues[0]?.[keyToUse]?.map((domain) => ({
                                                value: domain.key,
                                                label: domain.value,
                                              })) || []),
                                            ]}
                                            value={
                                              filters[keyToUse]
                                                ? { value: filters[keyToUse], label: filters[keyToUse] }
                                                : { value: "All", label: "All" }
                                            }
                                            onChange={(selectedOption) =>
                                              handleFilterChange(keyToUse, selectedOption?.label || "All")
                                            }
                                            components={{
                                              DropdownIndicator: CustomDropdownIndicator,
                                            }}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder={t("selectvalue", { attrName: keyToUse })}
                                          />
                                        ) : (
                                          // Render a text input for fields without Domains
                                          <input
                                            className="form-control"
                                            type="text"
                                            value={filters[keyToUse] || ""}
                                            onChange={(e) => handleFilterChange(keyToUse, e.target.value)}
                                          />
                                        )}
                                      </th>
                                    );
                                  })}
                              </tr>

                            )}
                          </thead>
                          <tbody>
                            {Array.isArray(InvertebratescurrentRows.current) ? (
                              InvertebratescurrentRows.current.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    <input type="checkbox" className="form-check-input" checked={InvertebratesSelectedRows.includes(row.id)} onChange={() => handleRowSelection(row.id)} disabled={isEditing} />
                                  </td>
                                  {Object.keys(row).filter((key) => key !== "OBJECTID" && key != "id" && key !== "SpeciesOrder").map((key) => {
                                    const filteredAttributes = window.InvertiblesInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'ObservationDate' && attr.fieldname !== 'OBJECTID' && attr.fieldname !== 'SpeciesOrder' && attr.fieldname === key);
                                    return (
                                      <td key={key}>
                                        {isEditing && editingRows.includes(row.id) && InvertebratesSelectedRows.includes(row.id) && filteredAttributes.length > 0 ? (
                                          filteredAttributes[0].Domains === false ? (
                                            key.includes("Date") ? (
                                              <input className="form-control" type="text" defaultValue={new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ')} style={{ pointerEvents: "none" }} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                            ) : (
                                              <input className="form-control" type="text" defaultValue={row[key]} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                            )) : (
                                            <input className="form-control" type="text" value={row[key]} onClick={() => handleFieldClick(row.id, key, 'domain')} readOnly />
                                          )
                                        ) : (
                                          key.includes("Date") ? new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ') : (
                                            <div className="tableTxtTruncate">{row[key]}</div>
                                          )
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="12">{t('No data available')}</td>
                              </tr>
                            )}

                          </tbody>
                        </table>
                      </div>
                      {InvertebratescurrentRows && InvertebratescurrentRows.current &&
                        <AnimalSightingMobileApproved
                          className="mobileOnly"
                          commonProps={commonProps}
                          IsDataModified={isDataModified}
                          t={t}
                          i18n={i18n}
                          GetFamilyDropdown={GetFamilyDropdown}
                          GetSpeciesDropdown={GetSpeciesDropdown}
                          DropdownFamilyValues={DropdownFamilyValues}
                          DropdownSpeciesValues={DropdownSpeciesValues}
                          domainValues={InsectsDomainValues[0]}
                          setActiveApprovedtab={setActiveApprovedtab}
                          activeApprovedtabs={activeApprovedtabs}
                          data={InvertebratescurrentRows}
                          fullView={fullView}
                        />
                      }
                      {/* {editingCardId && currentSelectedRow && isMobile && widgetMobEl && createPortal( */}
                      {modalVisible && (
                        isMobile && widgetMobEl ? createPortal(
                          <div className="aniOverlay a3">
                            <div className="modal-dialog popcontainer">
                              <div className="modal-content p-3 modal-change-themeBg">
                                <div className="modal-header  border-bottom-0 ">
                                  <div className="mb-1">
                                    <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                  </div>
                                </div>
                                <div className="modal-body pb-0 pt-2">
                                  {currentEdit.type === 'text' ? (
                                    <>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        {/* <input type="text" value={InvertebratescurrentRows.current[currentEdit.rowId - 1][currentEdit.key]} disabled /><br /> */}
                                        <input className="form-control" type="text" value={InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <input className="form-control" type="text" value={ApprovedInsectsData[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                      </div>
                                    </>
                                  ) : currentEdit.type === 'domain' ? (
                                    <>
                                      {currentEdit.key === "Species_Order" ? (
                                        <>
                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                            <Select
                                              value={
                                                InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  ? {
                                                    value: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                    label: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  }
                                                  : null
                                              }
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              isDisabled={true}
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label htmlFor="speciesOrder"><b>{t("Species Order")}:</b></label>
                                            <Select
                                              options={InsectsDomainValues[0].SpeciesOrder.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.Species_Order}
                                              onChange={(selectedOption) => GetFamilyDropdown(selectedOption ? selectedOption.value : '')}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Insect Order")}
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label htmlFor="speciesFamily"><b>{t("Species Family")}:</b></label>
                                            <Select
                                              options={DropdownFamilyValues.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.SpeciesFamily}
                                              onChange={(selectedOption) => GetSpeciesDropdown(selectedOption ? selectedOption.value : '')}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Family")}
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label htmlFor="speciesName"><b>{t("Species Name")}:</b></label>
                                            <Select
                                              options={DropdownSpeciesValues.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.SpeciesName ? { value: ApprovedInsectsData.SpeciesName, label: ApprovedInsectsData.SpeciesName } : null}
                                              onChange={(selectedOption) => handleInsectInputChange(selectedOption, "SpeciesName")}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Species Name")}
                                            />
                                          </div>

                                        </>
                                      ) : currentEdit.key === "SpeciesFamily" ? (
                                        <>

                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                            <Select
                                              value={
                                                InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  ? {
                                                    value: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                    label: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  }
                                                  : null
                                              }
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              isDisabled={true}
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label htmlFor="speciesFamily"><b>{t("Species Family")}:</b></label>
                                            <Select
                                              options={dropdownValues}
                                              value={ApprovedInsectsData.SpeciesFamily}
                                              onChange={(selectedOption) => GetSpeciesDropdown(selectedOption ? selectedOption.value : '')}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label htmlFor="speciesName"><b>{t("Species Name")}:</b></label>
                                            <Select
                                              options={DropdownSpeciesValues.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.SpeciesName ? { value: ApprovedInsectsData.SpeciesName, label: ApprovedInsectsData.SpeciesName } : null}
                                              onChange={(selectedOption) => handleInsectInputChange(selectedOption, "SpeciesName")}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Species Name")}
                                            />
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                            <Select
                                              value={
                                                InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  ? {
                                                    value: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                    label: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  }
                                                  : null
                                              }
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              isDisabled={true}
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                            <Select
                                              options={dropdownValues}
                                              value={ApprovedInsectsData[currentEdit.key] ? { value: ApprovedInsectsData[currentEdit.key], label: ApprovedInsectsData[currentEdit.key] } : null}
                                              onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                            />
                                          </div>
                                        </>
                                      )}
                                    </>
                                  ) : null}
                                </div>
                                <div className="modal-footer px-0 d-flex gap-2 pt-2">
                                  <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                                </div>
                              </div>
                            </div>
                          </div>, widgetMobEl
                        ) : (
                          <div className="aniOverlay a3web">
                            <div className="modal-dialog popcontainer">
                              <div className="modal-content p-3 modal-change-themeBg">
                                <div className="modal-header  border-bottom-0 ">
                                  <div className="mb-1">
                                    <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                  </div>
                                </div>
                                <div className="modal-body pb-0 pt-2">
                                  {currentEdit.type === 'text' ? (
                                    <>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        {/* <input type="text" value={InvertebratescurrentRows.current[currentEdit.rowId - 1][currentEdit.key]} disabled /><br /> */}
                                        <input className="form-control" type="text" value={InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <input className="form-control" type="text" value={ApprovedInsectsData[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                      </div>
                                    </>
                                  ) : currentEdit.type === 'domain' ? (
                                    <>
                                      {currentEdit.key === "Species_Order" ? (
                                        <>
                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                            <Select
                                              value={
                                                InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  ? {
                                                    value: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                    label: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  }
                                                  : null
                                              }
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              isDisabled={true}
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label htmlFor="speciesOrder"><b>{t("Species Order")}:</b></label>
                                            <Select
                                              options={InsectsDomainValues[0].SpeciesOrder.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.Species_Order}
                                              onChange={(selectedOption) => GetFamilyDropdown(selectedOption ? selectedOption.value : '')}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Insect Order")}
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label htmlFor="speciesFamily"><b>{t("Species Family")}:</b></label>
                                            <Select
                                              options={DropdownFamilyValues.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.SpeciesFamily}
                                              onChange={(selectedOption) => GetSpeciesDropdown(selectedOption ? selectedOption.value : '')}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Family")}
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label htmlFor="speciesName"><b>{t("Species Name")}:</b></label>
                                            <Select
                                              options={DropdownSpeciesValues.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.SpeciesName ? { value: ApprovedInsectsData.SpeciesName, label: ApprovedInsectsData.SpeciesName } : null}
                                              onChange={(selectedOption) => handleInsectInputChange(selectedOption, "SpeciesName")}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Species Name")}
                                            />
                                          </div>

                                        </>
                                      ) : currentEdit.key === "SpeciesFamily" ? (
                                        <>

                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                            <Select
                                              value={
                                                InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  ? {
                                                    value: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                    label: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  }
                                                  : null
                                              }
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              isDisabled={true}
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label htmlFor="speciesFamily"><b>{t("Species Family")}:</b></label>
                                            <Select
                                              options={dropdownValues}
                                              value={ApprovedInsectsData.SpeciesFamily}
                                              onChange={(selectedOption) => GetSpeciesDropdown(selectedOption ? selectedOption.value : '')}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label htmlFor="speciesName"><b>{t("Species Name")}:</b></label>
                                            <Select
                                              options={DropdownSpeciesValues.map((option) => ({
                                                value: option.key,
                                                label: option.value,
                                              }))}
                                              value={ApprovedInsectsData.SpeciesName ? { value: ApprovedInsectsData.SpeciesName, label: ApprovedInsectsData.SpeciesName } : null}
                                              onChange={(selectedOption) => handleInsectInputChange(selectedOption, "SpeciesName")}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              placeholder={t("Select Species Name")}
                                            />
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                            <Select
                                              value={
                                                InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  ? {
                                                    value: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                    label: InvertebratescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                                  }
                                                  : null
                                              }
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                              isDisabled={true}
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                            <Select
                                              options={dropdownValues}
                                              value={ApprovedInsectsData[currentEdit.key] ? { value: ApprovedInsectsData[currentEdit.key], label: ApprovedInsectsData[currentEdit.key] } : null}
                                              onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                              components={{
                                                DropdownIndicator: CustomDropdownIndicator,
                                              }}
                                              className={`react-select-container`}
                                              classNamePrefix="react-select"
                                            />
                                          </div>
                                        </>
                                      )}
                                    </>
                                  ) : null}
                                </div>
                                <div className="modal-footer px-0 d-flex gap-2 pt-2">
                                  <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      )}


                      {/* Filter on mobile view  -- start*/}

                      {mobFilterShow &&
                        <Modal className="aniMobModalLeft" show={mobFilterShow} onHide={handleMobFilterClose} centered>
                          <Modal.Header closeButton>
                            <Modal.Title>{t("filter")}</Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <div className="aniMobFilterContent">
                              <div>
                                <>
                                  {(() => {
                                    const tabMappings = {
                                      [t("Invertebrates")]: {
                                        currentRows: InvertebratescurrentRows,
                                        domainValues: InsectsDomainValues,
                                        displayAttributes: window.InvertiblesInfo.TargetFieldMapping,
                                      },
                                      [t("Plants")]: {
                                        currentRows: PlantscurrentRows,
                                        domainValues: PlantsDomainValues,
                                        displayAttributes: window.PlantInfo.TargetFieldMapping,
                                      },
                                      [t("Mammals")]: {
                                        currentRows: MammalscurrentRows,
                                        domainValues: MammalsDomainValues,
                                        displayAttributes: window.MammalsInfo.TargetFieldMapping,
                                      },
                                      [t("Reptiles")]: {
                                        currentRows: ReptilescurrentRows,
                                        domainValues: ReptilesDomainValues,
                                        displayAttributes: window.ReptilesInfo.TargetFieldMapping,
                                      },
                                    };

                                    // Dynamically get the currentRows and related data for the active tab
                                    const { currentRows, domainValues, displayAttributes } =
                                      tabMappings[activeApprovedtabs] || {};

                                    const formatKeyLabel = (key) => {
                                      const fixedLabels = {
                                        "point_x": "Point_X",
                                        "point_y": "Point_Y",
                                      };
                                      const lowerKey = key.toLowerCase();
                                      if (fixedLabels[lowerKey]) {
                                        return fixedLabels[lowerKey];
                                      }
                                      return key
                                        .replace(/_/g, ' ')                          // Replace underscores with spaces
                                        .replace(/([a-z])([A-Z])/g, '$1 $2')         // Space between camelCase
                                        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
                                    };

                                    return (
                                      mobFilterShow &&
                                      currentRows &&
                                      currentRows.current &&
                                      currentRows.current[0] &&
                                      Object.keys(currentRows.current[0] || originalData)
                                        .filter(
                                          (key) =>
                                            key !== "OBJECTID" &&
                                            key !== "id" &&
                                            key !== "created_date" &&
                                            key !== "SpeciesOrder" &&
                                            key !== "GlobalID"
                                        )
                                        .map((key) => {
                                          const filteredAttributes = displayAttributes.filter(
                                            (attr) =>
                                              attr.fieldname !== "id" &&
                                              attr.fieldname !== "ObservationDate" &&
                                              attr.fieldname !== "OBJECTID" &&
                                              attr.fieldname !== "GlobalID" &&
                                              attr.fieldname !== "SpeciesOrder" &&
                                              attr.fieldname !== "created_date" &&
                                              attr.fieldname === key
                                          );

                                          const keyToUse = key === "Species_Order" ? "SpeciesOrder" : key;

                                          return (
                                            <div className="card-container mb-3" key={`${keyToUse}-filter`}>
                                              <div className="card-row" key={keyToUse}>
                                                {filteredAttributes.length > 0 &&
                                                  filteredAttributes[0].Domains === true ? (
                                                  <>
                                                    <label>{formatKeyLabel(key)}</label>
                                                    <Select
                                                      options={[
                                                        { value: "All", label: "All" },
                                                        ...(domainValues[0]?.[keyToUse]?.map((domain) => ({
                                                          value: domain.key,
                                                          label: domain.value,
                                                        })) || []),
                                                      ]}
                                                      value={
                                                        filters[keyToUse]
                                                          ? {
                                                            value: filters[keyToUse],
                                                            label: filters[keyToUse],
                                                          }
                                                          : {
                                                            value: "All",
                                                            label: "All",
                                                          }
                                                      }
                                                      onChange={(selectedOption) =>
                                                        handleMobFilterChange(
                                                          keyToUse,
                                                          selectedOption?.label || "All"
                                                        )
                                                      }
                                                      components={{
                                                        DropdownIndicator: CustomDropdownIndicator,
                                                      }}
                                                      className={`react-select-container`}
                                                      classNamePrefix="react-select"
                                                      placeholder={`Select ${keyToUse}`}
                                                    />
                                                  </>
                                                ) : (
                                                  <div className="aniMobEditInputBlk">
                                                    <label>{formatKeyLabel(key)}</label>
                                                    <input
                                                      type="text"
                                                      value={filters[keyToUse] || ""}
                                                      className="form-control InputTextHolder"
                                                      onChange={(e) =>
                                                        handleMobFilterChange(keyToUse, e.target.value)
                                                      }
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )
                                        })
                                    )
                                  })()}
                                </>
                              </div>
                            </div>
                          </Modal.Body>
                          <Modal.Footer>
                            <Button variant="outline" onClick={handleMobFilterReset}>
                              {t("reset")}
                            </Button>
                            <Button
                              variant="primary"
                              onClick={() => {
                                setMobFilterShow(false);
                                handleFilterApply();
                              }}
                            >
                              {t("ApplyFilter")}
                            </Button>
                          </Modal.Footer>
                        </Modal>
                      }
                      {isResetBtnShow && (
                        <button
                          className="btn clearfilter-btn ms-0"
                          onClick={handleClarFilter}
                        >
                          <span>{t("Clear Filters")}</span>
                          <ImCancelCircle size={15} />
                        </button>
                      )}

                      {/* <Modal show={mobFilterShow} className="aniFilterModal" onHide={handleMobFilterClose} centered>
       <Modal.Header closeButton>
         <Modal.Title>Filter</Modal.Title>
       </Modal.Header>
       <Modal.Body>
         <div className="aniMobFilterContent">
           <div className="aniMobEditInputBlk">
             <label>Species Order</label>
             <Select
               options={speciesOrder}
               placeholder="Choose a species"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Species Family</label>
             <Select
               options={speciesOrder}
               placeholder="Choose a family"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Species Name</label>
             <Select
               options={speciesOrder}
               placeholder="Choose a name"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Location</label>
             <Select
               options={speciesOrder}
               placeholder="Choose location"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Habitat</label>
             <Select
               options={speciesOrder}
               placeholder="Choose a habitat"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Habitat Status</label>
             <Select
               options={speciesOrder}
               placeholder="Choose habitat status"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Observation date</label>
             <Select
               options={speciesOrder}
               placeholder="Choose observation date"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Observation Date</label>
             <Select
               options={speciesOrder}
               placeholder="Choose observation date"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Researcher</label>
             <Select
               options={speciesOrder}
               placeholder="Choose researcher"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Notes</label>
             <Select
               options={speciesOrder}
               placeholder="Choose notes"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
           <div className="aniMobEditInputBlk">
             <label>Number of individuals</label>
             <Select
               options={speciesOrder}
               placeholder="Choose number of individuals"
               isClearable
               className={`react-select-container`}
               classNamePrefix="react-select"
             />
           </div>
         </div>
       </Modal.Body>
       <Modal.Footer>
         <Button variant="outline" onClick={handleMobFilterClose}>
           {t("reset")}
         </Button>
         <Button variant="primary" onClick={() => alert("Saved!")}>
           Apply
         </Button>
       </Modal.Footer>
     </Modal> */}
                      {/* Filter on mobile view  -- end*/}
                    </div>
                  </div>
                </TabPanel>
              )}
              {groupNames.includes(window.userGroups.plants) && (<TabPanel>
                <div className="approvedrecord-container pendingrecord-container">
                  <div className="animalFilterGrid animalFilterGrid--approved">
                    <h6 className="mb-3 mt-1 me-2" style={{ fontSize: '0.8rem' }}>{t('approvePlants')}</h6>
                    <div className="gap5 filterDateBlk">
                      <div className="animalFilterBlk--approved onshiftGaping">
                        <button className={`btn btn-outline approvedrecord-button ${showfilter ? "active" : ""}`}
                          onClick={isMobileView ? handleMobFilterShow : onFilterClick}
                          data-tooltip-content={t('filter')}
                          data-tooltip-id={`tooltip-filter`}
                          data-tooltip-place="bottom"
                        >
                          <span className="filter-text" style={{ color: theme === "dark" ? "#fff" : showfilter ? "#179075" : "#000" }}
                            ref={(el) => { if (el) elementInstances["span2"] = el; }}
                          >{t('filter')}</span>
                          <BiFilterAlt className="filter-icon" size={15} />
                        </button>
                        {/* <Tooltip id={`tooltip-filter`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                        {showfilter && (
                          <button className="btn clearfilter-btn" onClick={handleClarFilter}>
                            {t('reset')}
                            <ImCancelCircle size={15} style={{ marginLeft: "10px" }} />
                          </button>
                        )}
                      </div>
                      <button
                        className={`btn btn-outline btn-dateRange ${showDateRange ? "active" : ""}`}
                        onClick={handleDateRange}
                        data-tooltip-content={t("Date Range")}
                        data-tooltip-id={`tooltip-dateRange`}
                        data-tooltip-place="bottom"
                      >
                        <span>{t('date')}</span>
                        <RxCalendar size={15} />
                      </button>
                      {/* <Tooltip id={`tooltip-dateRange`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                      {(showDateRange || (fullView === "fullView" && !isMobileView)) && (
                        <div className="dateRangeBlk">
                          <div className="dateRangeHead d-flex justify-content-between aign-items-center">
                            <h6>{t('datesrange')}</h6>
                            <button className="crossBtn border-0" onClick={() => setShowDateRange(false)}>
                              <RxCross2 />
                            </button>
                          </div>
                          <div className="row dateRandeInputWrp">
                            <div className="col-md-6 mb-1">
                              <label>{t('fromDate')}:</label>
                              <input className="form-control" type="date" name="SurveyFromDate" value={datefilters.SurveyFromDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                            </div>
                            <div className="col-md-6 mb-1">
                              <label>{t('toDate')}:</label>
                              <input className="form-control" type="date" name="SurveyToDate" value={datefilters.SurveyToDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                            </div>
                          </div>
                          {/* <div className="d-flex justify-content-end mt-3 dateRangeFooter">
                          <button className="btn btn-primary" onClick={handleSearchDatafordates} disabled={isButtonDisabled}>{t('submit')}</button>
                        </div> */}

                        </div>
                      )}
                    </div>
                    <div className="gap5">
                      <button
                        className={`btn btn-outline ${(activeButton === "edit" && isEditing) ? "active" : ""}`}
                        onClick={() => handleButtonClick("edit", handleEditSelectedRows)}
                        data-tooltip-content={t("edit")}
                        data-tooltip-id={`tooltip-edit`}
                        data-tooltip-place="bottom"
                      >
                        <span>{t('edit')}</span>
                        <FiEdit2 size={15} />
                      </button>
                      {/* <Tooltip id={`tooltip-edit`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                      <button className="btn btn-outline cancel" onClick={handleCanceledting}>
                        <span>{t('cancel')}</span>
                      </button>
                    </div>
                  </div>
                  <div className="table-responsive Animalapprovedtable">
                    <div className="TableScrollDiv desktopOnly">
                      <table id="tblApprovedPlants" className="table textwrap" data-sort-asc="true">
                        <thead className="thead-dark">
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={
                                  PlantsSelectedRows.length > 0 &&
                                  PlantsSelectedRows.length === (PlantscurrentRows?.current || []).length
                                }
                                disabled={isEditing && editingRows.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const allRowIds = (PlantscurrentRows?.current || []).map(
                                      (row) => row.id // Assuming `id` is the unique identifier
                                    );
                                    setPlantsSelectedRows(allRowIds);
                                  } else {
                                    setPlantsSelectedRows([]);
                                  }
                                }}
                              />
                            </th>
                            {plantsheaders.map((key) => (
                              <th key={key} onClick={() => sortTable(key, "tblApprovedPlants")}>{key}</th>
                            ))}
                          </tr>
                          {showfilter && (
                            // <tr>
                            //   <th></th>
                            //   {PlantscurrentRows && PlantscurrentRows.current.length > 0 && PlantscurrentRows.current[0] ? (
                            //     Object.keys(PlantscurrentRows.current[0]).filter((key) => key !== "OBJECTID" && key != "id").map((key) => {
                            //       const filteredAttributes = window.PlantInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'SurveyDate' && attr.fieldname !== 'OBJECTID' && attr.fieldname === key);
                            //       return (
                            //         <th key={`${key}-filter`}>
                            //           {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (
                            //             <Select
                            //               options={[
                            //                 { value: "All", label: "All" },
                            //                 ...(PlantsDomainValues[0]?.[key === "LatinName" ? "PlantSpecies" : key]?.map((domain) => ({
                            //                   value: domain.key,
                            //                   label: domain.value,
                            //                 })) || []),
                            //               ]}
                            //               value={
                            //                 filters[key]
                            //                   ? {
                            //                     value: filters[key],
                            //                     label: filters[key]
                            //                   }
                            //                   : { value: "All", label: "All" }
                            //               }
                            //               onChange={(selectedOption) => handleFilterChange(key, selectedOption?.label || "All")}
                            //               components={{
                            //                 DropdownIndicator: CustomDropdownIndicator,
                            //               }}
                            //               className={`react-select-container`}
                            //               classNamePrefix="react-select"
                            //               placeholder={t("selectvalue", { attrName: key })}
                            //             //placeholder={t(`Select ${key}`)}
                            //             />
                            //           ) : (
                            //             <input className="form-control" type="text" value={filters[key] || ''} onChange={(e) => handleFilterChange(key, e.target.value)} />
                            //           )}
                            //         </th>
                            //       );
                            //     })
                            //   ) : (
                            //     <td colSpan={Object.keys(PlantscurrentRows.current[0] || {}).length}>
                            //       {t('No data available')}
                            //     </td>
                            //   )}
                            // </tr>

                            <tr>
                              <th></th>
                              {(
                                PlantscurrentRows.current.length > 0
                                  ? Object.keys(PlantscurrentRows.current[0]) // If rows exist, use the keys from data
                                  : window.PlantInfo.TargetFieldMapping.map((attr) => attr.fieldname) // Otherwise, fallback to TargetFieldMapping keys
                              )
                                .filter((key) => key !== "OBJECTID" && key !== "id" && key !== "FDC_Global_ID") // Filter out unwanted keys
                                .map((key) => {
                                  const filteredAttributes = window.PlantInfo.TargetFieldMapping.filter(
                                    (attr) =>
                                      attr.fieldname !== "id" &&
                                      attr.fieldname !== "SurveyDate" &&
                                      attr.fieldname !== "OBJECTID" &&
                                      attr.fieldname === key
                                  );

                                  return (
                                    <th key={`${key}-filter`}>
                                      {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (
                                        // Render a Select dropdown for fields with Domains
                                        <Select
                                          options={[
                                            { value: "All", label: "All" },
                                            ...(PlantsDomainValues[0]?.[
                                              key === "LatinName" ? "PlantSpecies" : key
                                            ]?.map((domain) => ({
                                              value: domain.key,
                                              label: domain.value,
                                            })) || []),
                                          ]}
                                          value={
                                            filters[key]
                                              ? { value: filters[key], label: filters[key] }
                                              : { value: "All", label: "All" }
                                          }
                                          onChange={(selectedOption) =>
                                            handleFilterChange(key, selectedOption?.label || "All")
                                          }
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className="react-select-container"
                                          classNamePrefix="react-select"
                                          placeholder={t("selectvalue", { attrName: key })}
                                        />
                                      ) : (
                                        // Render a text input for fields without Domains
                                        <input
                                          className="form-control"
                                          type="text"
                                          value={filters[key] || ""}
                                          onChange={(e) => handleFilterChange(key, e.target.value)}
                                        />
                                      )}
                                    </th>
                                  );
                                })}
                              {/* Always show the "No data available" message */}
                              {PlantscurrentRows.current.length === 0 && (
                                <td colSpan={window.PlantInfo.TargetFieldMapping.length} className="no-data-message">
                                  {t("No data available")}
                                </td>
                              )}
                            </tr>



                          )}
                        </thead>
                        <tbody>
                          {Array.isArray(PlantscurrentRows.current) ? (
                            PlantscurrentRows.current.map((row) => (
                              <tr key={row.id}>
                                <td>
                                  <input type="checkbox" className="form-check-input" checked={PlantsSelectedRows.includes(row.id)} onChange={() => handleRowSelection(row.id)} disabled={isEditing} />
                                </td>
                                {Object.keys(row).filter((key) => key !== "OBJECTID" && key != "id").map((key) => {
                                  const filteredAttributes = window.PlantInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'OBJECTID' && attr.fieldname !== 'SurveyDate' && attr.fieldname === key);
                                  return (
                                    <td key={key}>
                                      {isEditing && editingRows.includes(row.id) && PlantsSelectedRows.includes(row.id) && filteredAttributes.length > 0 ? (
                                        filteredAttributes[0].Domains === false ? (
                                          key.includes("Date") ? (
                                            <input className="form-control" type="text" defaultValue={new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ')} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                          ) : (
                                            <input className="form-control" type="text" defaultValue={row[key]} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                          )) : (
                                          <input className="form-control" type="text" value={row[key]} onClick={() => handleFieldClick(row.id, key, 'domain')} readOnly />
                                        )
                                      ) : (
                                        key.includes("Date") ? new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ') : (
                                          <div className="tableTxtTruncate">{row[key]}</div>
                                        )
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="12">{t('No data available')}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <AnimalSightingMobileApproved
                      className="mobileOnly"
                      commonProps={commonProps}
                      IsDataModified={isDataModified}
                      t={t}
                      i18n={i18n}
                      GetFamilyDropdown={GetFamilyDropdown}
                      GetSpeciesDropdown={GetSpeciesDropdown}
                      DropdownFamilyValues={DropdownFamilyValues}
                      DropdownSpeciesValues={DropdownSpeciesValues}
                      domainValues={PlantsDomainValues[0]}
                      setActiveApprovedtab={setActiveApprovedtab}
                      activeApprovedtabs={activeApprovedtabs}
                      data={PlantscurrentRows}
                      fullView={fullView}
                    />
                    {modalVisible && (
                      isMobile && widgetMobEl ? createPortal(
                        <div className="aniOverlay a3">
                          <div className="modal-dialog popcontainer">
                            <div className="modal-content p-3 modal-change-themeBg">
                              <div className="modal-header  border-bottom-0 ">
                                <div className="mb-1">
                                  <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                </div>
                              </div>
                              <div className="modal-body pb-0 pt-2">
                                {currentEdit.type === 'text' ? (
                                  <>
                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                      <input className="form-control" type="text" id="ReplaceCellValue" value={PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                    </div>

                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                      <input className="form-control" type="text" id="ReplacewithTextBox" value={ApprovedPlantsDataObj[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                    </div>
                                  </>
                                ) : currentEdit.type === 'domain' ? (
                                  <>
                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                      <Select
                                        value={
                                          PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                            ? {
                                              value: PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                              label: PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                            }
                                            : null
                                        }
                                        components={{
                                          DropdownIndicator: CustomDropdownIndicator,
                                        }}
                                        className={`react-select-container`}
                                        classNamePrefix="react-select"
                                        isDisabled={true}
                                      />
                                    </div>

                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                      <Select
                                        options={plantsdropdownValues}
                                        value={ApprovedPlantsDataObj[currentEdit.key] ? { value: ApprovedPlantsDataObj[currentEdit.key], label: ApprovedPlantsDataObj[currentEdit.key] } : null}
                                        onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                        components={{
                                          DropdownIndicator: CustomDropdownIndicator,
                                        }}
                                        className={`react-select-container`}
                                        classNamePrefix="react-select"
                                      />
                                    </div>
                                  </>
                                ) : null}
                              </div>
                              <div className="modal-footer px-0 d-flex gap-2 pt-4">
                                <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                              </div>
                            </div>
                          </div>
                        </div>, widgetMobEl
                      ) : (
                        <div className="aniOverlay a3web">
                          <div className="modal-dialog popcontainer">
                            <div className="modal-content p-3 modal-change-themeBg">
                              <div className="modal-header  border-bottom-0 ">
                                <div className="mb-1">
                                  <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                </div>
                              </div>
                              <div className="modal-body pb-0 pt-2">
                                {currentEdit.type === 'text' ? (
                                  <>
                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                      <input className="form-control" type="text" id="ReplaceCellValue" value={PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                    </div>

                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                      <input className="form-control" type="text" id="ReplacewithTextBox" value={ApprovedPlantsDataObj[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                    </div>
                                  </>
                                ) : currentEdit.type === 'domain' ? (
                                  <>
                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                      <Select
                                        value={
                                          PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                            ? {
                                              value: PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                              label: PlantscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                            }
                                            : null
                                        }
                                        components={{
                                          DropdownIndicator: CustomDropdownIndicator,
                                        }}
                                        className={`react-select-container`}
                                        classNamePrefix="react-select"
                                        isDisabled={true}
                                      />
                                    </div>

                                    <div className="form-group">
                                      <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                      <Select
                                        options={plantsdropdownValues}
                                        value={ApprovedPlantsDataObj[currentEdit.key] ? { value: ApprovedPlantsDataObj[currentEdit.key], label: ApprovedPlantsDataObj[currentEdit.key] } : null}
                                        onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                        components={{
                                          DropdownIndicator: CustomDropdownIndicator,
                                        }}
                                        className={`react-select-container`}
                                        classNamePrefix="react-select"
                                      />
                                    </div>
                                  </>
                                ) : null}
                              </div>
                              <div className="modal-footer px-0 d-flex gap-2 pt-4">
                                <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}

                    {mobFilterShow &&
                      <Modal className="aniMobModalLeft" show={mobFilterShow} onHide={handleMobFilterClose} centered>
                        <Modal.Header closeButton>
                          <Modal.Title>{t("filter")}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                          <div className="aniMobFilterContent">
                            <div>
                              <>
                                {(() => {
                                  const tabMappings = {
                                    [t("Invertebrates")]: {
                                      currentRows: InvertebratescurrentRows,
                                      domainValues: InsectsDomainValues,
                                      displayAttributes: window.InvertiblesInfo.TargetFieldMapping,
                                    },
                                    [t("Plants")]: {
                                      currentRows: PlantscurrentRows,
                                      domainValues: PlantsDomainValues,
                                      displayAttributes: window.PlantInfo.TargetFieldMapping,
                                    },
                                    [t("Mammals")]: {
                                      currentRows: MammalscurrentRows,
                                      domainValues: MammalsDomainValues,
                                      displayAttributes: window.MammalsInfo.TargetFieldMapping,
                                    },
                                    [t("Reptiles")]: {
                                      currentRows: ReptilescurrentRows,
                                      domainValues: ReptilesDomainValues,
                                      displayAttributes: window.ReptilesInfo.TargetFieldMapping,
                                    },
                                  };

                                  // Dynamically get the currentRows and related data for the active tab
                                  const { currentRows, domainValues, displayAttributes } =
                                    tabMappings[activeApprovedtabs] || {};

                                  const formatKeyLabel = (key) => {
                                    const fixedLabels = {
                                      "point_x": "Point_X",
                                      "point_y": "Point_Y",
                                    };
                                    const lowerKey = key.toLowerCase();
                                    if (fixedLabels[lowerKey]) {
                                      return fixedLabels[lowerKey];
                                    }
                                    return key
                                      .replace(/_/g, ' ')                          // Replace underscores with spaces
                                      .replace(/([a-z])([A-Z])/g, '$1 $2')         // Space between camelCase
                                      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
                                  };

                                  return (
                                    mobFilterShow &&
                                    currentRows &&
                                    currentRows.current &&
                                    currentRows.current[0] &&
                                    Object.keys(currentRows.current[0] || originalData)
                                      .filter(
                                        (key) =>
                                          key !== "OBJECTID" &&
                                          key !== "id" &&
                                          key !== "created_date" &&
                                          key !== "GlobalID"
                                      )
                                      .map((key) => {
                                        const filteredAttributes = displayAttributes.filter(
                                          (attr) =>
                                            attr.fieldname !== "id" &&
                                            attr.fieldname !== "OBJECTID" &&
                                            attr.fieldname !== "GlobalID" &&
                                            attr.fieldname !== "created_date" &&
                                            attr.fieldname === key
                                        );
                                        const keyToUse = key === "LatinName" ? "PlantSpecies" : key;
                                        return (
                                          <div className="card-container mb-3" key={`${keyToUse}-filter`}>
                                            <div className="card-row" key={keyToUse}>
                                              {filteredAttributes.length > 0 &&
                                                filteredAttributes[0].Domains === true ? (
                                                <>
                                                  <label>{formatKeyLabel(key)}</label>
                                                  <Select
                                                    options={[
                                                      { value: "All", label: "All" },
                                                      ...(domainValues[0]?.[keyToUse]?.map((domain) => ({
                                                        value: domain.key,
                                                        label: domain.value,
                                                      })) || []),
                                                    ]}
                                                    value={
                                                      filters[keyToUse]
                                                        ? {
                                                          value: filters[keyToUse],
                                                          label: filters[keyToUse],
                                                        }
                                                        : {
                                                          value: "All",
                                                          label: "All",
                                                        }
                                                    }
                                                    onChange={(selectedOption) =>
                                                      handleMobFilterChange(
                                                        keyToUse,
                                                        selectedOption?.label || "All"
                                                      )
                                                    }
                                                    components={{
                                                      DropdownIndicator: CustomDropdownIndicator,
                                                    }}
                                                    className={`react-select-container`}
                                                    classNamePrefix="react-select"
                                                    placeholder={`Select ${keyToUse}`}
                                                  />
                                                </>
                                              ) : (
                                                <div className="aniMobEditInputBlk">
                                                  <label>{formatKeyLabel(key)}</label>
                                                  <input
                                                    type="text"
                                                    value={filters[keyToUse] || ""}
                                                    className="form-control InputTextHolder"
                                                    onChange={(e) =>
                                                      handleMobFilterChange(keyToUse, e.target.value)
                                                    }
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      })
                                  )
                                })()}
                              </>
                            </div>
                          </div>
                        </Modal.Body>
                        <Modal.Footer>
                          <Button variant="outline" onClick={handleMobFilterReset}>
                            {t("reset")}
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() => {
                              setMobFilterShow(false);
                              handleFilterApply();
                            }}
                          >
                            {t("ApplyFilter")}
                          </Button>
                        </Modal.Footer>
                      </Modal>
                    }
                    {isResetBtnShow && (
                      <button
                        className="btn clearfilter-btn ms-0"
                        onClick={handleClarFilter}
                      >
                        <span>{t("Clear Filters")}</span>
                        <ImCancelCircle size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </TabPanel>)}
              {groupNames.includes(window.userGroups.mammals) && (
                <TabPanel>
                  <div className="approvedrecord-container pendingrecord-container">
                    <div className="animalFilterGrid animalFilterGrid--approved">
                      <h6 className="mb-3 mt-1 me-2" style={{ fontSize: '0.8rem' }}>{t('approveMammals')}</h6>
                      <div className="gap5 filterDateBlk">
                        <div className="animalFilterBlk--approved onshiftGaping">
                          <button className={`btn btn-outline approvedrecord-button ${showfilter ? "active" : ""}`}
                            onClick={isMobileView ? handleMobFilterShow : onFilterClick}
                            data-tooltip-content={t('filter')}
                            data-tooltip-id={`tooltip-filter`}
                            data-tooltip-place="bottom"
                          >
                            <span className="filter-text" style={{ color: theme === "dark" ? "#fff" : showfilter ? "#179075" : "#000" }}
                              ref={(el) => { if (el) elementInstances["span3"] = el; }}
                            >{t('filter')}</span>
                            <BiFilterAlt className="filter-icon" size={15} />
                          </button>
                          {/* <Tooltip id={`tooltip-filter`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                          {showfilter && (
                            <button className="btn clearfilter-btn" onClick={handleClarFilter}>
                              <span>{t('Clear Filters')}</span>
                              <ImCancelCircle size={15} />
                            </button>
                          )}
                        </div>
                        <button
                          className={`btn btn-outline btn-dateRange ${showDateRange ? "active" : ""}`}
                          onClick={handleDateRange}
                          data-tooltip-content={t("Date Range")}
                          data-tooltip-id={`tooltip-dateRange`}
                          data-tooltip-place="bottom"
                        >
                          <span>{t('date')}</span>
                          <RxCalendar size={15} />
                        </button>
                        {/* <Tooltip id={`tooltip-dateRange`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                        {(showDateRange || (fullView === "fullView" && !isMobileView)) && (
                          <div className="dateRangeBlk">
                            <div className="dateRangeHead d-flex justify-content-between aign-items-center">
                              <h6>{t('datesrange')}</h6>
                              <button className="crossBtn border-0" onClick={() => setShowDateRange(false)}>
                                <RxCross2 />
                              </button>
                            </div>
                            <div className="row dateRandeInputWrp">
                              <div className="col-md-6 mb-1">
                                <label>{t('fromDate')}:</label>
                                <input className="form-control" type="date" name="SurveyFromDate" value={datefilters.SurveyFromDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                              </div>
                              <div className="col-md-6 mb-1">
                                <label>{t('toDate')}:</label>
                                <input className="form-control" type="date" name="SurveyToDate" value={datefilters.SurveyToDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                              </div>
                            </div>
                            {/* <div className="d-flex justify-content-end mt-3 dateRangeFooter">
           <button className="btn btn-primary" onClick={handleSearchDatafordates} disabled={isButtonDisabled}>{t('submit')}</button>
         </div> */}

                          </div>
                        )}
                      </div>
                      <div className="gap5">
                        <button
                          className={`btn btn-outline ${(activeButton === "edit" && isEditing) ? "active" : ""}`}
                          onClick={() => handleButtonClick("edit", handleEditSelectedRows)}
                          data-tooltip-content={t("edit")}
                          data-tooltip-id={`tooltip-edit`}
                          data-tooltip-place="bottom"
                        >
                          <span>{t('edit')}</span>
                          <FiEdit2 size={15} />
                        </button>
                        {/* <Tooltip id={`tooltip-edit`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                        <button className="btn btn-outline cancel" onClick={handleCanceledting}>
                          <span>{t('cancel')}</span>
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive Animalapprovedtable">
                      <div className="TableScrollDiv desktopOnly">
                        <table id="tblApprovedMammals" className="table textwrap" data-sort-asc="true">
                          <thead className="thead-dark">
                            <tr>
                              <th>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={
                                    MammalsSelectedRows.length > 0 &&
                                    MammalsSelectedRows.length === (MammalscurrentRows?.current || []).length
                                  }
                                  disabled={isEditing && editingRows.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const allRowIds = (MammalscurrentRows?.current || []).map(
                                        (row) => row.id // Assuming `id` is the unique identifier
                                      );
                                      setMammalsSelectedRows(allRowIds);
                                    } else {
                                      setMammalsSelectedRows([]);
                                    }
                                  }}
                                />
                              </th>
                              {mammalsheaders.map((key) => (
                                <th key={key} onClick={() => sortTable(key, "tblApprovedMammals")}>{key}</th>
                              ))}
                            </tr>
                            {showfilter && (
                              // <tr>
                              //   <th></th>
                              //   {MammalscurrentRows && MammalscurrentRows.current.length > 0 && MammalscurrentRows.current[0] ? (
                              //     Object.keys(MammalscurrentRows.current[0]).filter((key) => key !== "OBJECTID" && key != "id").map((key) => {
                              //       const filteredAttributes = window.MammalsInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'SurveyDate' && attr.fieldname !== 'OBJECTID' && attr.fieldname === key);
                              //       return (
                              //         <th key={`${key}-filter`}>
                              //           {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (

                              //             <Select
                              //               options={[
                              //                 { value: "All", label: "All" },
                              //                 ...(MammalsDomainValues[0]?.[key]?.map((domain) => ({
                              //                   value: domain.key,
                              //                   label: domain.value,
                              //                 })) || []),
                              //               ]}
                              //               value={
                              //                 filters[key]
                              //                   ? {
                              //                     value: filters[key],
                              //                     label: filters[key]
                              //                   }
                              //                   : { value: "All", label: "All" }
                              //               }
                              //               onChange={(selectedOption) => handleFilterChange(key, selectedOption?.label || "All")}
                              //               components={{
                              //                 DropdownIndicator: CustomDropdownIndicator,
                              //               }}
                              //               className={`react-select-container`}
                              //               classNamePrefix="react-select"
                              //               placeholder={t("selectvalue", { attrName: key })}
                              //             //placeholder={t(`Select ${key}`)}
                              //             />
                              //           ) : (
                              //             <input className="form-control" type="text" value={filters[key] || ''} onChange={(e) => handleFilterChange(key, e.target.value)} />
                              //           )}
                              //         </th>
                              //       );
                              //     })
                              //   ) : (
                              //     <td colSpan={Object.keys(MammalscurrentRows.current[0] || {}).length}>
                              //       {t('No data available')}
                              //     </td>
                              //   )}
                              // </tr>

                              <tr>
                                <th></th>
                                {(
                                  // Always show filters, even if there are no rows
                                  MammalscurrentRows.current.length > 0
                                    ? Object.keys(MammalscurrentRows.current[0]) // If rows exist, use the keys from data
                                    : window.MammalsInfo.TargetFieldMapping.map((attr) => attr.fieldname) // Otherwise, fallback to TargetFieldMapping keys
                                )
                                  .filter((key) => key !== "OBJECTID" && key !== "id") // Filter out unwanted keys
                                  .map((key) => {
                                    const filteredAttributes = window.MammalsInfo.TargetFieldMapping.filter(
                                      (attr) =>
                                        attr.fieldname !== "id" &&
                                        attr.fieldname !== "SurveyDate" &&
                                        attr.fieldname !== "OBJECTID" &&
                                        attr.fieldname === key
                                    );

                                    return (
                                      <th key={`${key}-filter`}>
                                        {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (
                                          // Render a Select dropdown for fields with Domains
                                          <Select
                                            options={[
                                              { value: "All", label: "All" },
                                              ...(MammalsDomainValues[0]?.[
                                                key === "LatinName" ? "MammalSpecies" : key
                                              ]?.map((domain) => ({
                                                value: domain.key,
                                                label: domain.value,
                                              })) || []),
                                            ]}
                                            value={
                                              filters[key]
                                                ? { value: filters[key], label: filters[key] }
                                                : { value: "All", label: "All" }
                                            }
                                            onChange={(selectedOption) =>
                                              handleFilterChange(key, selectedOption?.label || "All")
                                            }
                                            components={{
                                              DropdownIndicator: CustomDropdownIndicator,
                                            }}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder={t("selectvalue", { attrName: key })}
                                          />
                                        ) : (
                                          // Render a text input for fields without Domains
                                          <input
                                            className="form-control"
                                            type="text"
                                            value={filters[key] || ""}
                                            onChange={(e) => handleFilterChange(key, e.target.value)}
                                          />
                                        )}
                                      </th>
                                    );
                                  })}
                                {/* Always show the "No data available" message when there is no data */}
                                {MammalscurrentRows.current.length === 0 && (
                                  <td colSpan={window.MammalsInfo.TargetFieldMapping.length} className="no-data-message">
                                    {t("No data available")}
                                  </td>
                                )}
                              </tr>
                            )}
                          </thead>
                          <tbody>
                            {Array.isArray(MammalscurrentRows.current) ? (
                              MammalscurrentRows.current.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    <input type="checkbox" className="form-check-input" checked={MammalsSelectedRows.includes(row.id)} onChange={() => handleRowSelection(row.id)} disabled={isEditing} />
                                  </td>
                                  {Object.keys(row).filter((key) => key !== "OBJECTID" && key != "id").map((key) => {
                                    const filteredAttributes = window.MammalsInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'OBJECTID' && attr.fieldname !== 'SurveyDate' && attr.fieldname === key);
                                    return (
                                      <td key={key}>
                                        {isEditing && editingRows.includes(row.id) && MammalsSelectedRows.includes(row.id) && filteredAttributes.length > 0 ? (
                                          filteredAttributes[0].Domains === false ? (
                                            key.includes("Date") ? (
                                              <input className="form-control" type="text" defaultValue={new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ')} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                            ) : (
                                              <input className="form-control" type="text" defaultValue={row[key]} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                            )) : (
                                            <input className="form-control" type="text" value={row[key]} onClick={() => handleFieldClick(row.id, key, 'domain')} readOnly />
                                          )
                                        ) : (
                                          key.includes("Date") ? new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ') : (
                                            <div className="tableTxtTruncate">{row[key]}</div>
                                          )
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="12">{t('No data available')}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <AnimalSightingMobileApproved
                        className="mobileOnly"
                        commonProps={commonProps}
                        t={t}
                        i18n={i18n}
                        IsDataModified={isDataModified}
                        GetFamilyDropdown={GetFamilyDropdown}
                        GetSpeciesDropdown={mammalsdropdownValues}
                        DropdownFamilyValues={DropdownFamilyValues}
                        DropdownSpeciesValues={mammalsdropdownValues}
                        domainValues={MammalsDomainValues[0]}
                        setActiveApprovedtab={setActiveApprovedtab}
                        activeApprovedtabs={activeApprovedtabs}
                        data={MammalscurrentRows}
                        fullView={fullView}
                      />
                      {modalVisible && (
                        isMobile && widgetMobEl ? createPortal(
                          <div className="aniOverlay a3">
                            <div className="modal-dialog popcontainer">
                              <div className="modal-content p-3 modal-change-themeBg">
                                <div className="modal-header  border-bottom-0 ">
                                  <div className="mb-1">
                                    <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                  </div>
                                </div>
                                <div className="modal-body pb-0 pt-2">
                                  {currentEdit.type === 'text' ? (
                                    <>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <input type="text" className="form-control" value={MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <input type="text" className="form-control" value={MammalsDataObj[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                      </div>
                                    </>
                                  ) : currentEdit.type === 'domain' ? (
                                    <>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <Select
                                          value={
                                            MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              ? {
                                                value: MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                label: MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              }
                                              : null
                                          }
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                          isDisabled={true}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <Select
                                          options={mammalsdropdownValues}
                                          value={MammalsDataObj[currentEdit.key] ? { value: MammalsDataObj[currentEdit.key], label: MammalsDataObj[currentEdit.key] } : null}
                                          onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                        />
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                                <div className="modal-footer px-0 d-flex gap-2 pt-4">
                                  <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                                </div>
                              </div>
                            </div>
                          </div>, widgetMobEl
                        ) : (
                          <div className="aniOverlay a3web">
                            <div className="modal-dialog popcontainer">
                              <div className="modal-content p-3 modal-change-themeBg">
                                <div className="modal-header  border-bottom-0 ">
                                  <div className="mb-1">
                                    <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                  </div>
                                </div>
                                <div className="modal-body pb-0 pt-2">
                                  {currentEdit.type === 'text' ? (
                                    <>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <input type="text" className="form-control" value={MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <input type="text" className="form-control" value={MammalsDataObj[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                      </div>
                                    </>
                                  ) : currentEdit.type === 'domain' ? (
                                    <>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <Select
                                          value={
                                            MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              ? {
                                                value: MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                label: MammalscurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              }
                                              : null
                                          }
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                          isDisabled={true}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <Select
                                          options={mammalsdropdownValues}
                                          value={MammalsDataObj[currentEdit.key] ? { value: MammalsDataObj[currentEdit.key], label: MammalsDataObj[currentEdit.key] } : null}
                                          onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                        />
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                                <div className="modal-footer px-0 d-flex gap-2 pt-4">
                                  <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                      {mobFilterShow &&
                        <Modal className="aniMobModalLeft" show={mobFilterShow} onHide={handleMobFilterClose} centered>
                          <Modal.Header closeButton>
                            <Modal.Title>{t("filter")}</Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <div className="aniMobFilterContent">
                              <div>
                                <>
                                  {(() => {
                                    const tabMappings = {
                                      [t("Invertebrates")]: {
                                        currentRows: InvertebratescurrentRows,
                                        domainValues: InsectsDomainValues,
                                        displayAttributes: window.InvertiblesInfo.TargetFieldMapping,
                                      },
                                      [t("Plants")]: {
                                        currentRows: PlantscurrentRows,
                                        domainValues: PlantsDomainValues,
                                        displayAttributes: window.PlantInfo.TargetFieldMapping,
                                      },
                                      [t("Mammals")]: {
                                        currentRows: MammalscurrentRows,
                                        domainValues: MammalsDomainValues,
                                        displayAttributes: window.MammalsInfo.TargetFieldMapping,
                                      },
                                      [t("Reptiles")]: {
                                        currentRows: ReptilescurrentRows,
                                        domainValues: ReptilesDomainValues,
                                        displayAttributes: window.ReptilesInfo.TargetFieldMapping,
                                      },
                                    };

                                    // Dynamically get the currentRows and related data for the active tab
                                    const { currentRows, domainValues, displayAttributes } =
                                      tabMappings[activeApprovedtabs] || {};

                                    const formatKeyLabel = (key) => {
                                      const fixedLabels = {
                                        "point_x": "Point_X",
                                        "point_y": "Point_Y",
                                      };
                                      const lowerKey = key.toLowerCase();
                                      if (fixedLabels[lowerKey]) {
                                        return fixedLabels[lowerKey];
                                      }
                                      return key
                                        .replace(/_/g, ' ')                          // Replace underscores with spaces
                                        .replace(/([a-z])([A-Z])/g, '$1 $2')         // Space between camelCase
                                        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
                                    };

                                    return (
                                      mobFilterShow &&
                                      currentRows &&
                                      currentRows.current &&
                                      currentRows.current[0] &&
                                      Object.keys(currentRows.current[0] || originalData)
                                        .filter(
                                          (key) =>
                                            key !== "OBJECTID" &&
                                            key !== "id" &&
                                            key !== "created_date" &&
                                            key !== "GlobalID"
                                        )
                                        .map((key) => {
                                          const filteredAttributes = displayAttributes.filter(
                                            (attr) =>
                                              attr.fieldname !== "id" &&
                                              attr.fieldname !== "OBJECTID" &&
                                              attr.fieldname !== "GlobalID" &&
                                              attr.fieldname !== "created_date" &&
                                              attr.fieldname === key
                                          );

                                          return (
                                            <div className="card-container mb-3" key={`${key}-filter`}>
                                              <div className="card-row" key={key}>
                                                {filteredAttributes.length > 0 &&
                                                  filteredAttributes[0].Domains === true ? (
                                                  <>
                                                    <label>{formatKeyLabel(key)}</label>
                                                    <Select
                                                      options={[
                                                        { value: "All", label: "All" },
                                                        ...(domainValues[0]?.[key]?.map((domain) => ({
                                                          value: domain.key,
                                                          label: domain.value,
                                                        })) || []),
                                                      ]}
                                                      value={
                                                        filters[key]
                                                          ? {
                                                            value: filters[key],
                                                            label: filters[key],
                                                          }
                                                          : {
                                                            value: "All",
                                                            label: "All",
                                                          }
                                                      }
                                                      onChange={(selectedOption) =>
                                                        handleMobFilterChange(
                                                          key,
                                                          selectedOption?.label || "All"
                                                        )
                                                      }
                                                      components={{
                                                        DropdownIndicator: CustomDropdownIndicator,
                                                      }}
                                                      className={`react-select-container`}
                                                      classNamePrefix="react-select"
                                                      placeholder={`Select ${key}`}
                                                    />
                                                  </>
                                                ) : (
                                                  <div className="aniMobEditInputBlk">
                                                    <label>{formatKeyLabel(key)}</label>
                                                    <input
                                                      type="text"
                                                      value={filters[key] || ""}
                                                      className="form-control InputTextHolder"
                                                      onChange={(e) =>
                                                        handleMobFilterChange(key, e.target.value)
                                                      }
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )
                                        })
                                    )
                                  })()}
                                </>
                              </div>
                            </div>
                          </Modal.Body>
                          <Modal.Footer>
                            <Button variant="outline" onClick={handleMobFilterReset}>
                              {t("reset")}
                            </Button>
                            <Button
                              variant="primary"
                              onClick={() => {
                                setMobFilterShow(false);
                                handleFilterApply();
                              }}
                            >
                              {t("ApplyFilter")}
                            </Button>
                          </Modal.Footer>
                        </Modal>
                      }
                      {isResetBtnShow && (
                        <button
                          className="btn clearfilter-btn ms-0"
                          onClick={handleClarFilter}
                        >
                          <span>{t("Clear Filters")}</span>
                          <ImCancelCircle size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </TabPanel>
              )}
              {groupNames.includes(window.userGroups.reptiles) && (
                <TabPanel>
                  <div className="approvedrecord-container pendingrecord-container">
                    <div className="animalFilterGrid animalFilterGrid--approved">
                      <h6 className="mb-3 mt-1 me-2" style={{ fontSize: '0.8rem' }}>{t('approveReptiles')}</h6>
                      <div className="gap5 filterDateBlk">
                        <div className="animalFilterBlk--approved onshiftGaping">
                          <button className={`btn btn-outline approvedrecord-button ${showfilter ? "active" : ""}`}
                            onClick={isMobileView ? handleMobFilterShow : onFilterClick}
                            data-tooltip-content={t('filter')}
                            data-tooltip-id={`tooltip-filter`}
                            data-tooltip-place="bottom"
                          >
                            <span className="filter-text" style={{ color: theme === "dark" ? "#fff" : showfilter ? "#179075" : "#000" }}
                              ref={(el) => { if (el) elementInstances["span4"] = el; }}
                            >{t('filter')}</span>
                            <BiFilterAlt className="filter-icon" size={15} />
                          </button>
                          {/* <Tooltip id={`tooltip-filter`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                          {showfilter && (
                            <button className="btn clearfilter-btn" onClick={handleClarFilter}>
                              <span>{t('Clear Filters')}</span>
                              <ImCancelCircle size={15} />
                            </button>
                          )}
                        </div>
                        <button
                          className={`btn btn-outline btn-dateRange ${showDateRange ? "active" : ""}`}
                          onClick={handleDateRange}
                          data-tooltip-content={t("Date Range")}
                          data-tooltip-id={`tooltip-dateRange`}
                          data-tooltip-place="bottom"
                        >
                          <span>{t('date')}</span>
                          <RxCalendar size={15} />
                        </button>
                        {/* <Tooltip id={`tooltip-dateRange`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                        {(showDateRange || (fullView === "fullView" && !isMobileView)) && (
                          <div className="dateRangeBlk">
                            <div className="dateRangeHead d-flex justify-content-between aign-items-center">
                              <h6>{t('datesrange')}</h6>
                              <button className="crossBtn border-0" onClick={() => setShowDateRange(false)}>
                                <RxCross2 />
                              </button>
                            </div>
                            <div className="row dateRandeInputWrp">
                              <div className="col-md-6 mb-1">
                                <label>{t('fromDate')}:</label>
                                <input className="form-control" type="date" name="SurveyFromDate" value={datefilters.SurveyFromDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                              </div>
                              <div className="col-md-6 mb-1">
                                <label>{t('toDate')}:</label>
                                <input className="form-control" type="date" name="SurveyToDate" value={datefilters.SurveyToDate} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
                              </div>
                            </div>
                            {/* <div className="d-flex justify-content-end mt-3 dateRangeFooter">
             <button className="btn btn-primary" onClick={handleSearchDatafordates} disabled={isButtonDisabled}>{t('submit')}</button>
           </div> */}

                          </div>
                        )}
                      </div>
                      <div className="gap5">
                        <button
                          className={`btn btn-outline ${(activeButton === "edit" && isEditing) ? "active" : ""}`}
                          onClick={() => handleButtonClick("edit", handleEditSelectedRows)}
                          data-tooltip-content={t("edit")}
                          data-tooltip-id={`tooltip-edit`}
                          data-tooltip-place="bottom"
                        >
                          <span>{t('edit')}</span>
                          <FiEdit2 size={15} />
                        </button>
                        {/* <Tooltip id={`tooltip-edit`} className="ToolTipEnvi" border="1px solid #2d787f" /> */}
                        <button className="btn btn-outline cancel" onClick={handleCanceledting}>
                          <span>{t('cancel')}</span>
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive Animalapprovedtable">
                      <div className="TableScrollDiv desktopOnly">
                        <table id="tblApprovedReptiles" className="table textwrap" data-sort-asc="true">
                          <thead className="thead-dark">
                            <tr>
                              <th>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={
                                    ReptilesSelectedRows.length > 0 &&
                                    ReptilesSelectedRows.length === (ReptilescurrentRows?.current || []).length
                                  }
                                  disabled={isEditing && editingRows.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const allRowIds = (ReptilescurrentRows?.current || []).map(
                                        (row) => row.id // Assuming `id` is the unique identifier
                                      );
                                      setReptilesSelectedRows(allRowIds);
                                    } else {
                                      setReptilesSelectedRows([]);
                                    }
                                  }}
                                />
                              </th>
                              {reptilesheaders.map((key) => (
                                <th key={key} onClick={() => sortTable(key, "tblApprovedReptiles")}>{key}</th>
                              ))}
                            </tr>
                            {showfilter && (
                              // <tr>
                              //   <th></th>
                              //   {ReptilescurrentRows && ReptilescurrentRows.current.length > 0 && ReptilescurrentRows.current[0] ? (
                              //     Object.keys(ReptilescurrentRows.current[0]).filter((key) => key !== "OBJECTID" && key != "id").map((key) => {
                              //       const filteredAttributes = window.ReptilesInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'ObservationDate' && attr.fieldname !== 'OBJECTID' && attr.fieldname === key);
                              //       return (
                              //         <th key={`${key}-filter`}>
                              //           {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (
                              //             <Select
                              //               options={[
                              //                 { value: "All", label: "All" },
                              //                 ...(ReptilesDomainValues[0]?.[key]?.map((domain) => ({
                              //                   value: domain.key,
                              //                   label: domain.value,
                              //                 })) || []),
                              //               ]}
                              //               value={
                              //                 filters[key]
                              //                   ? {
                              //                     value: filters[key],
                              //                     label: filters[key]
                              //                   }
                              //                   : { value: "All", label: "All" }
                              //               }
                              //               onChange={(selectedOption) => handleFilterChange(key, selectedOption?.label || "All")}
                              //               components={{
                              //                 DropdownIndicator: CustomDropdownIndicator,
                              //               }}
                              //               className={`react-select-container`}
                              //               classNamePrefix="react-select"
                              //               placeholder={t("selectvalue", { attrName: key })}
                              //             //placeholder={t(`Select ${key}`)}
                              //             />
                              //           ) : (
                              //             <input className="form-control" type="text" value={filters[key] || ''} onChange={(e) => handleFilterChange(key, e.target.value)} />
                              //           )}
                              //         </th>
                              //       );
                              //     })
                              //   ) : (
                              //     <td colSpan={Object.keys(ReptilescurrentRows.current[0] || {}).length}>
                              //       {t('No data available')}
                              //     </td>
                              //   )}
                              // </tr>

                              <tr>
                                <th></th>
                                {(
                                  // Always show filters, even if there are no rows
                                  ReptilescurrentRows.current.length > 0
                                    ? Object.keys(ReptilescurrentRows.current[0]) // If rows exist, use the keys from data
                                    : window.ReptilesInfo.TargetFieldMapping.map((attr) => attr.fieldname) // Otherwise, fallback to TargetFieldMapping keys
                                )
                                  .filter((key) => key !== "OBJECTID" && key !== "id") // Filter out unwanted keys
                                  .map((key) => {
                                    const filteredAttributes = window.ReptilesInfo.TargetFieldMapping.filter(
                                      (attr) =>
                                        attr.fieldname !== "id" &&
                                        attr.fieldname !== "ObservationDate" &&
                                        attr.fieldname !== "OBJECTID" &&
                                        attr.fieldname === key
                                    );

                                    return (
                                      <th key={`${key}-filter`}>
                                        {filteredAttributes.length > 0 && filteredAttributes[0].Domains === true ? (
                                          // Render a Select dropdown for fields with Domains
                                          <Select
                                            options={[
                                              { value: "All", label: "All" },
                                              ...(ReptilesDomainValues[0]?.[
                                                key === "LatinName" ? "ReptileSpecies" : key
                                              ]?.map((domain) => ({
                                                value: domain.key,
                                                label: domain.value,
                                              })) || []),
                                            ]}
                                            value={
                                              filters[key]
                                                ? { value: filters[key], label: filters[key] }
                                                : { value: "All", label: "All" }
                                            }
                                            onChange={(selectedOption) =>
                                              handleFilterChange(key, selectedOption?.label || "All")
                                            }
                                            components={{
                                              DropdownIndicator: CustomDropdownIndicator,
                                            }}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder={t("selectvalue", { attrName: key })}
                                          />
                                        ) : (
                                          // Render a text input for fields without Domains
                                          <input
                                            className="form-control"
                                            type="text"
                                            value={filters[key] || ""}
                                            onChange={(e) => handleFilterChange(key, e.target.value)}
                                          />
                                        )}
                                      </th>
                                    );
                                  })}
                                {/* Always show the "No data available" message when there is no data */}
                                {ReptilescurrentRows.current.length === 0 && (
                                  <td colSpan={window.ReptilesInfo.TargetFieldMapping.length} className="no-data-message">
                                    {t("No data available")}
                                  </td>
                                )}
                              </tr>

                            )}
                          </thead>
                          <tbody>
                            {Array.isArray(ReptilescurrentRows.current) ? (
                              ReptilescurrentRows.current.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    <input type="checkbox" className="form-check-input" checked={ReptilesSelectedRows.includes(row.id)} onChange={() => handleRowSelection(row.id)} disabled={isEditing} />
                                  </td>
                                  {Object.keys(row).filter((key) => key !== "OBJECTID" && key != "id").map((key) => {
                                    const filteredAttributes = window.ReptilesInfo.TargetFieldMapping.filter(attr => attr.fieldname !== 'id' && attr.fieldname !== 'OBJECTID' && attr.fieldname !== 'ObservationDate' && attr.fieldname === key);
                                    return (
                                      <td key={key}>
                                        {isEditing && editingRows.includes(row.id) && ReptilesSelectedRows.includes(row.id) && filteredAttributes.length > 0 ? (
                                          filteredAttributes[0].Domains === false ? (
                                            key.includes("Date") ? (
                                              <input className="form-control" type="text" defaultValue={new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ')} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                            ) : (
                                              <input className="form-control" type="text" defaultValue={row[key]} onClick={() => handleFieldClick(row.id, key, 'text')} readOnly />
                                            )) : (
                                            <input className="form-control" type="text" value={row[key]} onClick={() => handleFieldClick(row.id, key, 'domain')} readOnly />
                                          )
                                        ) : (
                                          key.includes("Date") ? new Date(row[key]).toISOString().slice(0, 19).replace('T', ' ') : (
                                            <div className="tableTxtTruncate">{row[key]}</div>
                                          )
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="12">{t('No data available')}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <AnimalSightingMobileApproved
                        className="mobileOnly"
                        commonProps={commonProps}
                        IsDataModified={isDataModified}
                        t={t}
                        i18n={i18n}
                        domainValues={ReptilesDomainValues[0]}
                        setActiveApprovedtab={setActiveApprovedtab}
                        activeApprovedtabs={activeApprovedtabs}
                        data={ReptilescurrentRows}
                        fullView={fullView}
                      />
                      {modalVisible && (
                        isMobile && widgetMobEl ? createPortal(
                          <div className="aniOverlay a3">
                            <div className="modal-dialog popcontainer">
                              <div className="modal-content p-3 modal-change-themeBg">
                                <div className="modal-header  border-bottom-0 ">
                                  <div className="mb-1">
                                    <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                  </div>
                                </div>
                                <div className="modal-body pb-0 pt-2">
                                  {currentEdit.type === 'text' ? (
                                    <>
                                      <div className="form-group">

                                        <label className="form-control" htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <input className="form-control" type="text" value={ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        {/* <input type="text" id="fieldValue" ref={fieldValueRef} /> */}
                                        <input className="form-control" type="text" value={ReptilesDataObj[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                      </div>

                                    </>
                                  ) : currentEdit.type === 'domain' ? (
                                    <>

                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <Select
                                          value={
                                            ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              ? {
                                                value: ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                label: ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              }
                                              : null
                                          }
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                          isDisabled={true}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <Select
                                          options={reptilesdropdownValues}
                                          value={ReptilesDataObj[currentEdit.key] ? { value: ReptilesDataObj[currentEdit.key], label: ReptilesDataObj[currentEdit.key] } : null}
                                          onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                        />
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                                <div className="modal-footer px-0 d-flex gap-2 pt-4">
                                  <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                                </div>
                              </div>
                            </div>
                          </div>, widgetMobEl
                        ) : (
                          <div className="aniOverlay a3web">
                            <div className="modal-dialog popcontainer">
                              <div className="modal-content p-3 modal-change-themeBg">
                                <div className="modal-header  border-bottom-0 ">
                                  <div className="mb-1">
                                    <h5 className="modal-title"><b>{t("Replace Fields")}</b></h5>
                                  </div>
                                </div>
                                <div className="modal-body pb-0 pt-2">
                                  {currentEdit.type === 'text' ? (
                                    <>
                                      <div className="form-group">

                                        <label className="form-control" htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <input className="form-control" type="text" value={ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key] || ''} disabled />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        {/* <input type="text" id="fieldValue" ref={fieldValueRef} /> */}
                                        <input className="form-control" type="text" value={ReptilesDataObj[currentEdit.key] || ''} onChange={(e) => handleInsectInputChange(e, currentEdit.key)} name={currentEdit.key} />
                                      </div>

                                    </>
                                  ) : currentEdit.type === 'domain' ? (
                                    <>

                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('replace')}:</b></label>
                                        <Select
                                          value={
                                            ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              ? {
                                                value: ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key],
                                                label: ReptilescurrentRows.current.find(item => item.id === currentEdit.rowId)?.[currentEdit.key]
                                              }
                                              : null
                                          }
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                          isDisabled={true}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label htmlFor="fieldValue"><b>{t('with')}:</b></label>
                                        <Select
                                          options={reptilesdropdownValues}
                                          value={ReptilesDataObj[currentEdit.key] ? { value: ReptilesDataObj[currentEdit.key], label: ReptilesDataObj[currentEdit.key] } : null}
                                          onChange={(selectedOption) => handleInsectInputChange(selectedOption, currentEdit.key)}
                                          components={{
                                            DropdownIndicator: CustomDropdownIndicator,
                                          }}
                                          className={`react-select-container`}
                                          classNamePrefix="react-select"
                                        />
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                                <div className="modal-footer px-0 d-flex gap-2 pt-4">
                                  <button className="btn btn-outline" onClick={handleCancelClick}>{t('cancel')}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceField}>{t("replace")}</button>
                                  <button className="btn btn-primary" onClick={handleReplaceAllField}>{t("replaceall")}</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                      {mobFilterShow &&
                        <Modal className="aniMobModalLeft" show={mobFilterShow} onHide={handleMobFilterClose} centered>
                          <Modal.Header closeButton>
                            <Modal.Title>{t("filter")}</Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <div className="aniMobFilterContent">
                              <div>
                                <>
                                  {(() => {
                                    const tabMappings = {
                                      [t("Invertebrates")]: {
                                        currentRows: InvertebratescurrentRows,
                                        domainValues: InsectsDomainValues,
                                        displayAttributes: window.InvertiblesInfo.TargetFieldMapping,
                                      },
                                      [t("Plants")]: {
                                        currentRows: PlantscurrentRows,
                                        domainValues: PlantsDomainValues,
                                        displayAttributes: window.PlantInfo.TargetFieldMapping,
                                      },
                                      [t("Mammals")]: {
                                        currentRows: MammalscurrentRows,
                                        domainValues: MammalsDomainValues,
                                        displayAttributes: window.MammalsInfo.TargetFieldMapping,
                                      },
                                      [t("Reptiles")]: {
                                        currentRows: ReptilescurrentRows,
                                        domainValues: ReptilesDomainValues,
                                        displayAttributes: window.ReptilesInfo.TargetFieldMapping,
                                      },
                                    };

                                    // Dynamically get the currentRows and related data for the active tab
                                    const { currentRows, domainValues, displayAttributes } =
                                      tabMappings[activeApprovedtabs] || {};

                                    const formatKeyLabel = (key) => {
                                      const fixedLabels = {
                                        "point_x": "Point_X",
                                        "point_y": "Point_Y",
                                      };
                                      const lowerKey = key.toLowerCase();
                                      if (fixedLabels[lowerKey]) {
                                        return fixedLabels[lowerKey];
                                      }
                                      return key
                                        .replace(/_/g, ' ')                          // Replace underscores with spaces
                                        .replace(/([a-z])([A-Z])/g, '$1 $2')         // Space between camelCase
                                        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
                                    };

                                    return (
                                      mobFilterShow &&
                                      currentRows &&
                                      currentRows.current &&
                                      currentRows.current[0] &&
                                      Object.keys(currentRows.current[0] || originalData)
                                        .filter(
                                          (key) =>
                                            key !== "OBJECTID" &&
                                            key !== "id" &&
                                            key !== "created_date" &&
                                            key !== "GlobalID"
                                        )
                                        .map((key) => {
                                          const filteredAttributes = displayAttributes.filter(
                                            (attr) =>
                                              attr.fieldname !== "id" &&
                                              attr.fieldname !== "OBJECTID" &&
                                              attr.fieldname !== "GlobalID" &&
                                              attr.fieldname !== "created_date" &&
                                              attr.fieldname === key
                                          );

                                          const keyToUse = key === "SpeciesName" ? "Species" : key;
                                          return (
                                            <div className="card-container mb-3" key={`${keyToUse}-filter`}>
                                              <div className="card-row" key={keyToUse}>
                                                {filteredAttributes.length > 0 &&
                                                  filteredAttributes[0].Domains === true ? (
                                                  <>
                                                    <label>{formatKeyLabel(key)}</label>
                                                    <Select
                                                      options={[
                                                        { value: "All", label: "All" },
                                                        ...(domainValues[0]?.[keyToUse]?.map((domain) => ({
                                                          value: domain.key,
                                                          label: domain.value,
                                                        })) || []),
                                                      ]}
                                                      value={
                                                        filters[keyToUse]
                                                          ? {
                                                            value: filters[keyToUse],
                                                            label: filters[keyToUse],
                                                          }
                                                          : {
                                                            value: "All",
                                                            label: "All",
                                                          }
                                                      }
                                                      onChange={(selectedOption) =>
                                                        handleMobFilterChange(
                                                          keyToUse,
                                                          selectedOption?.label || "All"
                                                        )
                                                      }
                                                      components={{
                                                        DropdownIndicator: CustomDropdownIndicator,
                                                      }}
                                                      className={`react-select-container`}
                                                      classNamePrefix="react-select"
                                                      placeholder={`Select ${keyToUse}`}
                                                    />
                                                  </>
                                                ) : (
                                                  <div className="aniMobEditInputBlk">
                                                    <label>{formatKeyLabel(key)}</label>
                                                    <input
                                                      type="text"
                                                      value={filters[keyToUse] || ""}
                                                      className="form-control InputTextHolder"
                                                      onChange={(e) =>
                                                        handleMobFilterChange(keyToUse, e.target.value)
                                                      }
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )
                                        })
                                    )
                                  })()}
                                </>
                              </div>
                            </div>
                          </Modal.Body>
                          <Modal.Footer>
                            <Button variant="outline" onClick={handleMobFilterReset}>
                              {t("reset")}
                            </Button>
                            <Button
                              variant="primary"
                              onClick={() => {
                                setMobFilterShow(false);
                                handleFilterApply();
                              }}
                            >
                              {t("ApplyFilter")}
                            </Button>
                          </Modal.Footer>
                        </Modal>
                      }
                      {isResetBtnShow && (
                        <button
                          className="btn clearfilter-btn ms-0"
                          onClick={handleClarFilter}
                        >
                          <span>{t("Clear Filters")}</span>
                          <ImCancelCircle size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </TabPanel>
              )}

            </Tabs>
          </>
        ) : (
          <DivLoader />
        )}
      </div>
    </>
  );
};
export default AnimalApprovedRecords;